import * as React from 'react';
import { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import UpdateIcon from '@mui/icons-material/Update';
import RefreshIcon from '@mui/icons-material/Refresh';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CallOutlinedIcon from '@mui/icons-material/CallOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import Dropdown from 'react-bootstrap/Dropdown';
import Modal from 'react-bootstrap/Modal';
import Table from 'react-bootstrap/Table';
import { postRequest, deleteRequest } from '../../../lib/api';
import { API_BASE } from '../../../lib/config';
import BreadcrumbsWrapper from '../../../components/breadcrumbs';
import { AuthContext } from '../../../authContext/context';
import { checkPermission } from '../../../utils/checkPermissions';
import Swal from 'sweetalert2';
import jsPDF from 'jspdf';
import Notes from '../Notes/note';
import ViewQuoteDetailModal from '../../../components/ViewQuoteDetailModal';

// Revise Quote Modal Component for Transfer of Equity
function ReviseQuoteModal({ show, onHide, leadData, authToken, onReviseQuote, quoteFeeData }) {
  const [fees, setFees] = useState({
    legal_fee: 0,
    leasehold_fee: 0,
    tt_fee: 0,
    identity_check: 0,
    mortgage_admin_fee: 0
  });

  const [disbursements, setDisbursements] = useState({
    hm_land_registry_fee: 0,
    land_registry_search: 0,
    land_charges_search: 0,
    official_copies: 0,
    local_search_insurance: 0,
    stamp_duty_land_tax: 0
  });

  const [totals, setTotals] = useState({
    net: 0,
    vat: 0,
    gross: 0,
    legalCost: { net: 0, vat: 0, gross: 0 },
    disbursementCost: { net: 0, vat: 0, gross: 0 }
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  // Fetch data from final_leads_calculations when modal opens
  React.useEffect(() => {
    if (show && leadData?.lead_id) {
      const fetchCalculationData = async () => {
        try {
          setIsFetching(true);
          const token = authToken || localStorage.getItem('token');

          console.log('📋 [Revise Modal] Fetching from fetch_lead_calculations for lead:', leadData.lead_id);

          // Get partner_id if exists
          const partnerId = leadData.partner_id?._id || leadData.partner_id;
          console.log('🤝 [Revise Modal] Partner ID:', partnerId);

          const API_BASE_URL = `${API_BASE}/v1/admin/`;
          const fetchUrl = partnerId
            ? `${API_BASE_URL}private/fetch_lead_calculations/${leadData.lead_id}?partner_id=${partnerId}`
            : `${API_BASE_URL}private/fetch_lead_calculations/${leadData.lead_id}`;

          console.log('📞 [Revise Modal] Fetching from:', fetchUrl);

          const response = await fetch(fetchUrl, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          });

          const result = await response.json();
          console.log('📡 [Revise Modal] API Response:', result);

          if (result.status === 200 && result.data) {
            const { fees: apiFees, disbursements: apiDisbursements, totals: apiTotals } = result.data;

            // Load fees
            if (apiFees) {
              // ✅ TT Fee - Check ALL possible key variations
              const ttFeeKey = Object.keys(apiFees).find(key =>
                key.toLowerCase().includes('telegraphic') ||
                key.toLowerCase() === 'ttfee' ||
                key.toLowerCase() === 'tt_fee' ||
                key.toLowerCase().includes('tt fee')
              );
              const ttFeeAmount = ttFeeKey ? (apiFees[ttFeeKey]?.amount || 0) : 0;

              setFees({
                legal_fee: apiFees.legalFee?.amount || 0,
                leasehold_fee: apiFees.leaseholdFee?.amount || apiFees.LeaseholdFee?.amount || 0,
                tt_fee: ttFeeAmount,
                identity_check: apiFees.identityCheck?.amount || apiFees.IdentityCheck?.amount || 0,
                mortgage_admin_fee: apiFees.mortgageAdminFee?.amount || apiFees.MortgageAdminFee?.amount || 0
              });
            }

            // Load disbursements
            if (apiDisbursements) {
              setDisbursements({
                hm_land_registry_fee: apiDisbursements.hmLandRegistryFee?.amount || apiDisbursements.HmLandRegistryFee?.amount || 0,
                land_registry_search: apiDisbursements.landRegistrySearch?.amount || apiDisbursements.LandRegistrySearch?.amount || 0,
                land_charges_search: apiDisbursements.landChargesSearch?.amount || apiDisbursements.LandChargesSearch?.amount || 0,
                official_copies: apiDisbursements.officialCopies?.amount || apiDisbursements.OfficialCopies?.amount || 0,
                local_search_insurance: apiDisbursements.localSearchInsurance?.amount || apiDisbursements.LocalSearchInsurance?.amount || 0,
                stamp_duty_land_tax: apiDisbursements.stampDutyLandTax?.amount || apiDisbursements.StampDutyLandTax?.amount || 0
              });
            }

            // Load totals - ensure proper structure with nested objects
            if (apiTotals) {
              setTotals({
                net: apiTotals.net || 0,
                vat: apiTotals.vat || 0,
                gross: apiTotals.gross || 0,
                legalCost: apiTotals.legalCost || { net: 0, vat: 0, gross: 0 },
                disbursementCost: apiTotals.disbursementCost || { net: 0, vat: 0, gross: 0 }
              });
            }

            console.log('✅ [Revise Modal] Data loaded from fetch_lead_calculations');
          }
        } catch (error) {
          console.error('❌ [Revise Modal] Error fetching data:', error);
        } finally {
          setIsFetching(false);
        }
      };

      fetchCalculationData();
    }
  }, [leadData?.lead_id, show]);

  // Calculate totals
  const calculateTotals = (newFees, newDisbursements) => {
    const feeObj = newFees || fees;
    const disbObj = newDisbursements || disbursements;

    // Legal fees with VAT
    const legalFeeNet = Number(feeObj.legal_fee) || 0;
    const leaseholdFeeNet = Number(feeObj.leasehold_fee) || 0;
    const ttFeeNet = Number(feeObj.tt_fee) || 0;
    const mortgageAdminFeeNet = Number(feeObj.mortgage_admin_fee) || 0;
    const identityCheckNet = Number(feeObj.identity_check) || 0;

    // Disbursements (mostly no VAT)
    const hmLandRegistryFeeNet = Number(disbObj.hm_land_registry_fee) || 0;
    const landRegistrySearchNet = Number(disbObj.land_registry_search) || 0;
    const landChargesSearchNet = Number(disbObj.land_charges_search) || 0;
    const officialCopiesNet = Number(disbObj.official_copies) || 0;
    const localSearchInsuranceNet = Number(disbObj.local_search_insurance) || 0;
    const stampDutyLandTaxNet = Number(disbObj.stamp_duty_land_tax) || 0;

    // Calculate VAT (20% on fees, some disbursements have VAT)
    const legalFeeVAT = legalFeeNet * 0.20;
    const leaseholdFeeVAT = leaseholdFeeNet * 0.20;
    const ttFeeVAT = ttFeeNet * 0.20;
    const mortgageAdminFeeVAT = mortgageAdminFeeNet * 0.20;
    const landRegistrySearchVAT = landRegistrySearchNet * 0.20;
    const landChargesSearchVAT = landChargesSearchNet * 0.20;

    // Calculate Legal Cost (all fees before disbursements)
    const legalCostNet = legalFeeNet + leaseholdFeeNet + ttFeeNet + mortgageAdminFeeNet + identityCheckNet;
    const legalCostVAT = legalFeeVAT + leaseholdFeeVAT + ttFeeVAT + mortgageAdminFeeVAT;
    const legalCostGross = legalCostNet + legalCostVAT;

    // Calculate Disbursement Cost (Third Party Expenditure)
    const disbursementCostNet = hmLandRegistryFeeNet + landRegistrySearchNet + landChargesSearchNet +
                                officialCopiesNet + localSearchInsuranceNet + stampDutyLandTaxNet;
    const disbursementCostVAT = landRegistrySearchVAT + landChargesSearchVAT;
    const disbursementCostGross = disbursementCostNet + disbursementCostVAT;

    // Calculate Grand Totals
    const totalNet = legalCostNet + disbursementCostNet;
    const totalVAT = legalCostVAT + disbursementCostVAT;
    const totalGross = totalNet + totalVAT;

    setTotals({
      net: totalNet,
      vat: totalVAT,
      gross: totalGross,
      legalCost: { net: legalCostNet, vat: legalCostVAT, gross: legalCostGross },
      disbursementCost: { net: disbursementCostNet, vat: disbursementCostVAT, gross: disbursementCostGross }
    });
  };

  // Recalculate when fees/disbursements change
  useEffect(() => {
    calculateTotals();
  }, [fees, disbursements]);

  const handleReviseQuote = async () => {
    try {
      setIsLoading(true);

      const payload = {
        fees: {
          legalFee: { amount: Number(fees.legal_fee), vat: Number(fees.legal_fee) * 0.20, gross: Number(fees.legal_fee) * 1.20, label: 'Legal Fee' },
          leaseholdFee: { amount: Number(fees.leasehold_fee), vat: Number(fees.leasehold_fee) * 0.20, gross: Number(fees.leasehold_fee) * 1.20, label: 'Leasehold Fee' },
          ttFee: { amount: Number(fees.tt_fee), vat: Number(fees.tt_fee) * 0.20, gross: Number(fees.tt_fee) * 1.20, label: 'TT Fee' },
          identityCheck: { amount: Number(fees.identity_check), vat: 0, gross: Number(fees.identity_check), label: 'Identity Check' },
          mortgageAdminFee: { amount: Number(fees.mortgage_admin_fee), vat: Number(fees.mortgage_admin_fee) * 0.20, gross: Number(fees.mortgage_admin_fee) * 1.20, label: 'Mortgage Admin Fee' }
        },
        disbursements: {
          hmLandRegistryFee: { amount: Number(disbursements.hm_land_registry_fee), vat: 0, gross: Number(disbursements.hm_land_registry_fee), label: 'HM Land Registry Fee' },
          landRegistrySearch: { amount: Number(disbursements.land_registry_search), vat: Number(disbursements.land_registry_search) * 0.20, gross: Number(disbursements.land_registry_search) * 1.20, label: 'Land Registry Search' },
          landChargesSearch: { amount: Number(disbursements.land_charges_search), vat: Number(disbursements.land_charges_search) * 0.20, gross: Number(disbursements.land_charges_search) * 1.20, label: 'Land Charges Search' },
          officialCopies: { amount: Number(disbursements.official_copies), vat: 0, gross: Number(disbursements.official_copies), label: 'Official Copies' },
          localSearchInsurance: { amount: Number(disbursements.local_search_insurance), vat: 0, gross: Number(disbursements.local_search_insurance), label: 'Local Search Insurance' },
          stampDutyLandTax: { amount: Number(disbursements.stamp_duty_land_tax), vat: 0, gross: Number(disbursements.stamp_duty_land_tax), label: 'Stamp Duty Land Tax' }
        },
        totals: totals
      };

      console.log('📝 [Revise Modal] Submitting revised quote for Transfer of Equity:', payload);
      await onReviseQuote(payload);

      onHide();
    } catch (error) {
      console.error('❌ [Revise Modal] Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered className="revise-quote-modal">
      <Modal.Header closeButton style={{ borderBottom: '1px solid #dee2e6' }}>
        <Modal.Title style={{ fontSize: '1.25rem', fontWeight: '600' }}>Revise Quote - Transfer of Equity</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: '1.5rem', maxHeight: '70vh', overflowY: 'auto' }}>

        <div style={{ marginBottom: '2rem' }}>
          <h5 style={{
            backgroundColor: '#0d6efd',
            color: 'white',
            padding: '10px 15px',
            marginBottom: '0',
            borderRadius: '4px 4px 0 0',
            fontWeight: '600'
          }}>
            Transfer of Equity
          </h5>
          <div className="table-responsive">
            <Table bordered hover style={{ marginBottom: 0 }}>
              <thead style={{ backgroundColor: '#f8f9fa' }}>
                <tr>
                  <th style={{ fontWeight: '600', fontSize: '0.95rem', padding: '12px 15px', width: '40%' }}>Description</th>
                  <th style={{ fontWeight: '600', fontSize: '0.95rem', textAlign: 'center', padding: '12px 15px' }}>Net</th>
                  <th style={{ fontWeight: '600', fontSize: '0.95rem', textAlign: 'center', padding: '12px 15px' }}>VAT</th>
                  <th style={{ fontWeight: '600', fontSize: '0.95rem', textAlign: 'center', padding: '12px 15px' }}>Gross</th>
                </tr>
              </thead>
              <tbody>
                {/* Legal Fees */}
                <tr style={{ backgroundColor: '#e7f1ff' }}>
                  <td colSpan="4" style={{ padding: '8px 15px', fontWeight: '600', fontSize: '0.95rem' }}>
                    Legal Fees
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '12px 15px', fontSize: '0.95rem' }}>Legal Fee</td>
                  <td style={{ padding: '8px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ marginRight: '5px' }}>£</span>
                      <input
                        type="number"
                        value={fees.legal_fee}
                        onChange={(e) => {
                          const newFees = { ...fees, legal_fee: e.target.value };
                          setFees(newFees);
                          calculateTotals(newFees, disbursements);
                        }}
                        style={{ width: '100px', padding: '5px', textAlign: 'right', border: '1px solid #ccc', borderRadius: '4px' }}
                      />
                    </div>
                  </td>
                  <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>
                    £{(Number(fees.legal_fee) * 0.20).toFixed(2)}
                  </td>
                  <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem', fontWeight: '600' }}>
                    £{(Number(fees.legal_fee) * 1.20).toFixed(2)}
                  </td>
                </tr>

                <tr>
                  <td style={{ padding: '12px 15px', fontSize: '0.95rem' }}>Leasehold Fee</td>
                  <td style={{ padding: '8px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ marginRight: '5px' }}>£</span>
                      <input
                        type="number"
                        value={fees.leasehold_fee}
                        onChange={(e) => {
                          const newFees = { ...fees, leasehold_fee: e.target.value };
                          setFees(newFees);
                          calculateTotals(newFees, disbursements);
                        }}
                        style={{ width: '100px', padding: '5px', textAlign: 'right', border: '1px solid #ccc', borderRadius: '4px' }}
                      />
                    </div>
                  </td>
                  <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>
                    £{(Number(fees.leasehold_fee) * 0.20).toFixed(2)}
                  </td>
                  <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem', fontWeight: '600' }}>
                    £{(Number(fees.leasehold_fee) * 1.20).toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '12px 15px', fontSize: '0.95rem' }}>TT Fee</td>
                  <td style={{ padding: '8px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ marginRight: '5px' }}>£</span>
                      <input
                        type="number"
                        value={fees.tt_fee}
                        onChange={(e) => {
                          const newFees = { ...fees, tt_fee: e.target.value };
                          setFees(newFees);
                          calculateTotals(newFees, disbursements);
                        }}
                        style={{ width: '100px', padding: '5px', textAlign: 'right', border: '1px solid #ccc', borderRadius: '4px' }}
                      />
                    </div>
                  </td>
                  <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>
                    £{(Number(fees.tt_fee) * 0.20).toFixed(2)}
                  </td>
                  <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem', fontWeight: '600' }}>
                    £{(Number(fees.tt_fee) * 1.20).toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '12px 15px', fontSize: '0.95rem' }}>Mortgage Admin Fee</td>
                  <td style={{ padding: '8px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ marginRight: '5px' }}>£</span>
                      <input
                        type="number"
                        value={fees.mortgage_admin_fee}
                        onChange={(e) => {
                          const newFees = { ...fees, mortgage_admin_fee: e.target.value };
                          setFees(newFees);
                          calculateTotals(newFees, disbursements);
                        }}
                        style={{ width: '100px', padding: '5px', textAlign: 'right', border: '1px solid #ccc', borderRadius: '4px' }}
                      />
                    </div>
                  </td>
                  <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>
                    £{(Number(fees.mortgage_admin_fee) * 0.20).toFixed(2)}
                  </td>
                  <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem', fontWeight: '600' }}>
                    £{(Number(fees.mortgage_admin_fee) * 1.20).toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '12px 15px', fontSize: '0.95rem' }}>Identity Check</td>
                  <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>
                    £{Number(fees.identity_check).toFixed(2)}
                  </td>
                  <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>£0.00</td>
                  <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem', fontWeight: '600' }}>
                    £{Number(fees.identity_check).toFixed(2)}
                  </td>
                </tr>

                {/* Disbursements */}
                <tr style={{ backgroundColor: '#e7f1ff' }}>
                  <td colSpan="4" style={{ padding: '8px 15px', fontWeight: '600', fontSize: '0.95rem' }}>
                    Disbursements
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '12px 15px', fontSize: '0.95rem' }}>HM Land Registry Fee</td>
                  <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>
                    £{Number(disbursements.hm_land_registry_fee).toFixed(2)}
                  </td>
                  <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>£0.00</td>
                  <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem', fontWeight: '600' }}>
                    £{Number(disbursements.hm_land_registry_fee).toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '12px 15px', fontSize: '0.95rem' }}>Land Registry Search</td>
                  <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>
                    £{Number(disbursements.land_registry_search).toFixed(2)}
                  </td>
                  <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>
                    £{(Number(disbursements.land_registry_search) * 0.20).toFixed(2)}
                  </td>
                  <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem', fontWeight: '600' }}>
                    £{(Number(disbursements.land_registry_search) * 1.20).toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '12px 15px', fontSize: '0.95rem' }}>Land Charges Search</td>
                  <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>
                    £{Number(disbursements.land_charges_search).toFixed(2)}
                  </td>
                  <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>
                    £{(Number(disbursements.land_charges_search) * 0.20).toFixed(2)}
                  </td>
                  <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem', fontWeight: '600' }}>
                    £{(Number(disbursements.land_charges_search) * 1.20).toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '12px 15px', fontSize: '0.95rem' }}>Official Copies</td>
                  <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>
                    £{Number(disbursements.official_copies).toFixed(2)}
                  </td>
                  <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>£0.00</td>
                  <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem', fontWeight: '600' }}>
                    £{Number(disbursements.official_copies).toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '12px 15px', fontSize: '0.95rem' }}>Local Search Insurance</td>
                  <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>
                    £{Number(disbursements.local_search_insurance).toFixed(2)}
                  </td>
                  <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>£0.00</td>
                  <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem', fontWeight: '600' }}>
                    £{Number(disbursements.local_search_insurance).toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '12px 15px', fontSize: '0.95rem' }}>Stamp Duty Land Tax</td>
                  <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>
                    £{Number(disbursements.stamp_duty_land_tax).toFixed(2)}
                  </td>
                  <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>£0.00</td>
                  <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem', fontWeight: '600' }}>
                    £{Number(disbursements.stamp_duty_land_tax).toFixed(2)}
                  </td>
                </tr>

                {/* TOTAL LEGAL COST */}
                <tr style={{ backgroundColor: '#fff3cd', fontWeight: '600', borderTop: '2px solid #dee2e6' }}>
                  <td style={{ padding: '12px 15px', fontSize: '0.95rem' }}>TOTAL LEGAL COST</td>
                  <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>
                    £{(totals.legalCost?.net || 0).toFixed(2)}
                  </td>
                  <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>
                    £{(totals.legalCost?.vat || 0).toFixed(2)}
                  </td>
                  <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>
                    £{(totals.legalCost?.gross || 0).toFixed(2)}
                  </td>
                </tr>

                {/* TOTAL DISBURSEMENT COST */}
                <tr style={{ backgroundColor: '#d1ecf1', fontWeight: '600' }}>
                  <td style={{ padding: '12px 15px', fontSize: '0.95rem' }}>TOTAL DISBURSEMENT COST</td>
                  <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>
                    £{(totals.disbursementCost?.net || 0).toFixed(2)}
                  </td>
                  <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>
                    £{(totals.disbursementCost?.vat || 0).toFixed(2)}
                  </td>
                  <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>
                    £{(totals.disbursementCost?.gross || 0).toFixed(2)}
                  </td>
                </tr>

                {/* Total (Legal cost + disbursement) */}
                <tr style={{ backgroundColor: '#d4edda', fontWeight: '700', fontSize: '1rem', borderTop: '2px solid #28a745' }}>
                  <td style={{ padding: '15px' }}>Total (Legal cost + disbursement)</td>
                  <td style={{ textAlign: 'center', padding: '15px' }}>
                    £{(totals?.net || 0).toFixed(2)}
                  </td>
                  <td style={{ textAlign: 'center', padding: '15px' }}>
                    £{(totals?.vat || 0).toFixed(2)}
                  </td>
                  <td style={{ textAlign: 'center', padding: '15px' }}>
                    £{(totals?.gross || 0).toFixed(2)}
                  </td>
                </tr>
              </tbody>
            </Table>
          </div>
        </div>

      </Modal.Body>
      <Modal.Footer style={{ borderTop: 'none', padding: '0 20px 20px 20px', backgroundColor: '#f8f9fa' }}>
        <Button
          variant="outline-secondary"
          onClick={onHide}
          style={{
            paddingLeft: '20px',
            paddingRight: '20px',
            fontWeight: '500'
          }}
        >
          Cancel
        </Button>
        <Button
          variant="danger"
          onClick={handleReviseQuote}
          disabled={isLoading}
          style={{
            paddingLeft: '20px',
            paddingRight: '20px',
            fontWeight: '500',
            backgroundColor: '#dc3545',
            borderColor: '#dc3545'
          }}
        >
          {isLoading ? 'Updating...' : 'Revise Quote'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

const ViewLeadTransferOfEquity = () => {
  const { lead_id } = useParams();
  const navigate = useNavigate();
  const authCtx = useContext(AuthContext);

  const [leadData, setLeadData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [canEditLeads, setCanEditLeads] = useState(false);
  const [canDeleteLeads, setCanDeleteLeads] = useState(false);
  const [canChangeGroupCode, setCanChangeGroupCode] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [quoteFeeData, setQuoteFeeData] = useState({
    ownership_type: null,
    branch_name: '',
    legalFee: { amount: 0, vat: 0, gross: 0 },
    fixedFees: [],
    disbursements: [],
    net: 0,
    vat: 0,
    gross: 0
  });

  console.log(quoteFeeData , 'quoteFeeDataquoteFeeData')
  const [loadingFee, setLoadingFee] = useState(false);
  const [partners, setPartners] = useState([]);

  // Communication / Email states
  const [emailLogs, setEmailLogs] = useState([]);
  const [loadingEmails, setLoadingEmails] = useState(false);
  const [viewEmailModal, setViewEmailModal] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [contactAttempts, setContactAttempts] = useState(0);
  const [reviseModalShow, setReviseModalShow] = useState(false);
  const [quoteHistory, setQuoteHistory] = useState([]);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showQuoteDetailModal, setShowQuoteDetailModal] = useState(false);

  useEffect(() => {
    const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
    setCanEditLeads(checkPermission(permissions, 'Leads', 'update'));
    setCanDeleteLeads(checkPermission(permissions, 'Leads', 'delete'));
    setCanChangeGroupCode(checkPermission(permissions, 'Leads', 'update'));
  }, []);

  useEffect(() => {
    if (lead_id && authCtx.token) {
      fetchLeadData();
      // fetchPartners();
    }
  }, [lead_id, authCtx.token]);

  // Fetch quote fee when leadData is loaded
  useEffect(() => {
    if (leadData && authCtx.token) {
      fetchQuoteFee();
      fetchEmailLogs();
      fetchEmailTemplates();
      setContactAttempts(leadData.contact_attempts || 0);
    }
  }, [leadData, authCtx.token]);

  // 📚 Fetch Quote History from Database
  const fetchQuoteHistory = async () => {
    if (!lead_id) return;

    try {
      console.log('📚 [Transfer of Equity] Fetching quote history from database...');

      const API_BASE_URL = `${API_BASE}/v1/admin/`;
      const response = await fetch(`${API_BASE_URL}private/getQuoteHistory/${lead_id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authCtx.token}`
        }
      });

      const result = await response.json();

      if (result.status === 200 && result.data) {
        console.log('✅ [Transfer of Equity] Quote history fetched:', result.data);
        setQuoteHistory(result.data);
      } else {
        console.warn('⚠️ [Transfer of Equity] No quote history found');
        setQuoteHistory([]);
      }
    } catch (error) {
      console.error('❌ [Transfer of Equity] Error fetching quote history:', error);
      setQuoteHistory([]);
    }
  };

  // Fetch quote history on component mount
  useEffect(() => {
    fetchQuoteHistory();
  }, [lead_id, authCtx.token]);

  // Helper function to format label from camelCase to Title Case
  const formatLabel = (label) => {
    if (!label) return '';

    // Convert camelCase to space-separated words
    let formatted = label
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();

    // Handle special acronyms and words
    formatted = formatted
      .replace(/\bIsa\b/g, 'ISA')
      .replace(/\bTt\b/g, 'TT')
      .replace(/\bHm\b/g, 'HM')
      .replace(/\bLand Registry\b/gi, 'Land Registry')
      .replace(/\bLand Charges\b/gi, 'Land Charges');

    return formatted;
  };

  const fetchQuoteFee = async () => {
    try {
      setLoadingFee(true);

      if (!leadData) {
        console.log('⚠️ No lead data available');
        return;
      }

      console.log('📞 Fetching calculation using fetch_lead_calculations API...');

      // Get partner_id if exists
      const partnerId = leadData.partner_id?._id || leadData.partner_id;
      console.log('🤝 Partner ID for fetch:', partnerId);

      // ✅ Use same API as Purchase/Sale - fetch from database
      const API_BASE_URL = `${API_BASE}/v1/admin/`;
      const fetchUrl = partnerId
        ? `${API_BASE_URL}private/fetch_lead_calculations/${lead_id}?partner_id=${partnerId}`
        : `${API_BASE_URL}private/fetch_lead_calculations/${lead_id}`;

      console.log('📞 Fetching from:', fetchUrl);

      const response = await fetch(fetchUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authCtx.token}`
        }
      });

      const res = await response.json();

      console.log('📡 API Response:', res);

      if (res?.status === 200 && res?.data) {
        console.log('✅ Calculation data loaded from database:', res.data);

        // ✅ Convert fees Map to array for display
        const feesArray = res.data.fees ? Object.values(res.data.fees) : [];

        // ✅ Convert disbursements Map to array for display
        const disbursementsArray = res.data.disbursements ? Object.values(res.data.disbursements) : [];

        // ✅ Use pre-calculated totals from database (nested structure)
        const totals = res.data.totals || { total: { net: 0, vat: 0, gross: 0 } };

        console.log('💰 Using database totals:', totals);
        console.log('📋 Fees:', feesArray.length, 'items');
        console.log('📋 Disbursements:', disbursementsArray.length, 'items');

        setQuoteFeeData({
          ownership_type: res.data.ownership_type,
          branch_name: res.data.branch_name || '',
          legalFee: feesArray.find(f => f.label?.includes('Legal Fee')) || { label: 'Legal Fee', amount: 0, vat: 0, gross: 0 },
          fixedFees: feesArray.filter(f => !f.label?.includes('Legal Fee')),
          disbursements: disbursementsArray,
          net: totals.total?.net || 0,
          vat: totals.total?.vat || 0,
          gross: totals.total?.gross || 0
        });
      } else {
        throw new Error(res?.message || 'Failed to fetch quote details');
      }

    } catch (err) {
      console.error('❌ Error fetching fee data:', err);
      Swal.fire('Error', err?.message || 'Failed to fetch quote details', 'error');
    } finally {
      setLoadingFee(false);
    }
  };

  const fetchLeadData = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching Transfer of Equity lead data for:', lead_id);

      // Step 1: Try to get basic lead data using get-single-lead (includes partner_id populated)
      let leadInfo = null;
      try {
        const leadRes = await postRequest(`private/get-single-lead/${lead_id}`, {}, authCtx.token);
        if (leadRes?.status === 'success' && leadRes?.data) {
          leadInfo = leadRes.data;
          console.log('✅ Basic lead info loaded:', leadInfo);
          console.log('👤 Partner info:', leadInfo.partner_id);
        }
      } catch (err) {
        console.warn('⚠️ Failed to get basic lead info:', err.message);
      }

      // Step 2: Try to get Transfer of Equity specific details from multiple endpoints
      const transferOfEquityEndpoints = [
        // { url: `private/getTransferOfEquityDetails/${lead_id}`, name: 'getTransferOfEquityDetails' },
        { url: `private/getConveyancingDetails/${lead_id}`, name: 'getConveyancingDetails' },
        { url: `private/conveyancing-transfer-of-equity/${lead_id}`, name: 'conveyancing-transfer-of-equity' }
      ];

      let transferDetails = null;
      for (const endpoint of transferOfEquityEndpoints) {
        try {
          console.log(`🔄 Trying ${endpoint.name}...`);
          const detailRes = await postRequest(endpoint.url, {}, authCtx.token);

          if (detailRes?.status === 200 && detailRes?.data) {
            console.log(`✅ Transfer of Equity details found via ${endpoint.name}:`, detailRes.data);
            transferDetails = detailRes.data;
            break;
          }
        } catch (err) {
          console.log(`❌ ${endpoint.name} failed:`, err.message);
        }
      }

      // Step 3: Merge data
      if (leadInfo || transferDetails) {
        const mergedData = {
          ...(leadInfo || {}),
          ...(transferDetails || {}),
          lead_id: lead_id // Ensure lead_id is set
        };

        // Preserve partner_id object from leadInfo if it's an object (populated)
        // transferDetails might have string ID which would override the populated object
        if (leadInfo && leadInfo.partner_id && typeof leadInfo.partner_id === 'object') {
          mergedData.partner_id = leadInfo.partner_id;
          console.log('✅ Preserved partner_id object from leadInfo:', leadInfo.partner_id);
        }

        // Preserve lender_panel_id object from leadInfo if it's an object (populated)
        if (leadInfo && leadInfo.lender_panel_id && typeof leadInfo.lender_panel_id === 'object') {
          mergedData.lender_panel_id = leadInfo.lender_panel_id;
          console.log('✅ Preserved lender_panel_id object from leadInfo:', leadInfo.lender_panel_id);
        }

        console.log('✅ Final merged data:', mergedData);
        console.log('📊 Data structure check:');
        console.log('  - partner_id:', mergedData.partner_id);
        console.log('  - total_quote:', mergedData.total_quote);
        console.log('  - legal_fee:', mergedData.legal_fee);
        console.log('  - disbursements:', mergedData.disbursements);
        console.log('  - property_address:', mergedData.property_address);
        console.log('  - customers_to_add:', mergedData.customers_to_add);
        console.log('  - customers_to_remove:', mergedData.customers_to_remove);
        console.log('  - property_value:', mergedData.property_value);
        console.log('  - transfer_type:', mergedData.transfer_type);
        console.log('  - consideration_value:', mergedData.consideration_value);
        console.log('  - ownership_type:', mergedData.ownership_type);
        console.log('  - is_mortgaged:', mergedData.is_mortgaged);

        setLeadData(mergedData);
      } else {
        console.warn('⚠️ No lead data found from any source');
        Swal.fire('Warning', 'No Transfer of Equity lead found', 'warning');
      }
    } catch (err) {
      console.error('❌ Error fetching lead data:', err);
      Swal.fire('Error', 'Failed to load lead data: ' + (err.message || 'Unknown error'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchPartners = async () => {
    try {
      const res = await postRequest('private/partnersList', {}, authCtx.token);
      if (res?.status === 200 && res?.data) {
        setPartners(res.data);
      }
    } catch (err) {
      console.error('Error fetching partners:', err);
    }
  };

  const handleDeleteLead = async () => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to delete this Transfer of Equity lead #${lead_id}? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        console.log('Attempting to delete lead with ID:', lead_id);

        // Use the DELETE method with fetch API (same as Sale & Purchase)
        const response = await fetch(`/api/v1/admin/private/delete-lead/${lead_id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${authCtx.token}`,
            'Content-Type': 'application/json'
          }
        });

        const data = await response.json();
        console.log('Delete response:', data);

        if (response.ok && (data.status === 200 || data.status === 'success')) {
          Swal.fire({
            icon: 'success',
            title: 'Deleted!',
            text: 'Transfer of Equity lead has been deleted successfully.'
          }).then(() => {
            navigate('/leads');
          });
        } else {
          throw new Error(data.message || 'Failed to delete lead');
        }
      } catch (error) {
        console.error('Error deleting lead:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.message || 'Failed to delete lead'
        });
      }
    }
  };

  // Handle revise quote
  const handleReviseQuote = async (payload) => {
    try {
      console.log('🔄 ===== REVISE QUOTE (Transfer of Equity - NEW API) =====');
      console.log('📦 Received payload from modal:', payload);

      // Transform flat fees to NESTED format (already nested from modal, but ensure proper structure)
      const nestedFees = {
        legalFee: { amount: Number(payload.fees.legalFee?.amount || 0), vat: Number(payload.fees.legalFee?.amount || 0) * 0.20, gross: Number(payload.fees.legalFee?.amount || 0) * 1.20, label: 'Legal Fee' },
        leaseholdFee: { amount: Number(payload.fees.leaseholdFee?.amount || 0), vat: Number(payload.fees.leaseholdFee?.amount || 0) * 0.20, gross: Number(payload.fees.leaseholdFee?.amount || 0) * 1.20, label: 'Leasehold Fee' },
        ttFee: { amount: Number(payload.fees.ttFee?.amount || 0), vat: Number(payload.fees.ttFee?.amount || 0) * 0.20, gross: Number(payload.fees.ttFee?.amount || 0) * 1.20, label: 'TT Fee' },
        identityCheck: { amount: Number(payload.fees.identityCheck?.amount || 0), vat: 0, gross: Number(payload.fees.identityCheck?.amount || 0), label: 'Identity Check' },
        mortgageAdminFee: { amount: Number(payload.fees.mortgageAdminFee?.amount || 0), vat: Number(payload.fees.mortgageAdminFee?.amount || 0) * 0.20, gross: Number(payload.fees.mortgageAdminFee?.amount || 0) * 1.20, label: 'Mortgage Admin Fee' }
      };

      const nestedDisbursements = {
        hmLandRegistryFee: { amount: Number(payload.disbursements.hmLandRegistryFee?.amount || 0), vat: 0, gross: Number(payload.disbursements.hmLandRegistryFee?.amount || 0), label: 'HM Land Registry Fee' },
        landRegistrySearch: { amount: Number(payload.disbursements.landRegistrySearch?.amount || 0), vat: Number(payload.disbursements.landRegistrySearch?.amount || 0) * 0.20, gross: Number(payload.disbursements.landRegistrySearch?.amount || 0) * 1.20, label: 'Land Registry Search' },
        landChargesSearch: { amount: Number(payload.disbursements.landChargesSearch?.amount || 0), vat: Number(payload.disbursements.landChargesSearch?.amount || 0) * 0.20, gross: Number(payload.disbursements.landChargesSearch?.amount || 0) * 1.20, label: 'Land Charges Search' },
        officialCopies: { amount: Number(payload.disbursements.officialCopies?.amount || 0), vat: 0, gross: Number(payload.disbursements.officialCopies?.amount || 0), label: 'Official Copies' },
        localSearchInsurance: { amount: Number(payload.disbursements.localSearchInsurance?.amount || 0), vat: 0, gross: Number(payload.disbursements.localSearchInsurance?.amount || 0), label: 'Local Search Insurance' },
        stampDutyLandTax: { amount: Number(payload.disbursements.stampDutyLandTax?.amount || 0), vat: 0, gross: Number(payload.disbursements.stampDutyLandTax?.amount || 0), label: 'Stamp Duty Land Tax' }
      };

      // Calculate Legal Cost Total
      const legalCostNet = Object.values(nestedFees).reduce((sum, fee) => sum + fee.amount, 0);
      const legalCostVat = Object.values(nestedFees).reduce((sum, fee) => sum + fee.vat, 0);
      const legalCostGross = Object.values(nestedFees).reduce((sum, fee) => sum + fee.gross, 0);

      // Calculate Disbursement Cost Total
      const disbursementCostNet = Object.values(nestedDisbursements).reduce((sum, fee) => sum + fee.amount, 0);
      const disbursementCostVat = Object.values(nestedDisbursements).reduce((sum, fee) => sum + fee.vat, 0);
      const disbursementCostGross = Object.values(nestedDisbursements).reduce((sum, fee) => sum + fee.gross, 0);

      // Calculate Grand Total
      const totalNet = legalCostNet + disbursementCostNet;
      const totalVat = legalCostVat + disbursementCostVat;
      const totalGross = legalCostGross + disbursementCostGross;

      const nestedTotals = {
        legalCost: { net: legalCostNet, vat: legalCostVat, gross: legalCostGross },
        disbursementCost: { net: disbursementCostNet, vat: disbursementCostVat, gross: disbursementCostGross },
        total: { net: totalNet, vat: totalVat, gross: totalGross }
      };

      console.log('✅ Nested format created:', { nestedFees, nestedDisbursements, nestedTotals });

      // Call NEW reviseQuote API
      const API_BASE_URL = `${API_BASE}/v1/admin/`;
      const apiUrl = `${API_BASE_URL}private/reviseQuote/${lead_id}`;
      console.log('📡 Calling NEW reviseQuote API:', apiUrl);

      const res = await fetch(apiUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authCtx.token}`
        },
        body: JSON.stringify({
          fees: nestedFees,
          disbursements: nestedDisbursements,
          totals: nestedTotals
        })
      });

      if (!res.ok) {
        const errorText = await res.text();
        console.error('❌ API Error Response:', errorText);
        throw new Error(`API call failed: ${res.status} - ${errorText}`);
      }

      const result = await res.json();
      console.log('✅ Revise Quote API Response:', result);

      if (result?.status === 200) {
        console.log('✅ Quote revised successfully!');

        // Fetch fresh quote history from database
        await fetchQuoteHistory();

        // Refresh active quote
        await fetchQuoteFee();

        Swal.fire('Success', 'Quote revised successfully!', 'success');

        // Close the revise quote modal
        setReviseModalShow(false);
      } else {
        Swal.fire('Error', result?.message || 'Failed to revise quote', 'error');
      }
    } catch (err) {
      console.error('❌ Error revising quote:', err);
      Swal.fire('Error', err?.message || 'Something went wrong', 'error');
    }
  };

  // Handle Regenerate Quote - Restore Original Quote
  const handleRegenerateQuote = async () => {
    try {
      const confirmResult = await Swal.fire({
        title: 'Regenerate Original Quote?',
        text: 'This will restore the original quote values. Current changes will be saved to history.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, regenerate it!'
      });

      if (!confirmResult.isConfirmed) {
        return;
      }

      console.log('🔁 ===== REGENERATE QUOTE (Transfer of Equity) =====');
      console.log('📋 Lead ID:', lead_id);

      // Call regenerateQuote API
      const API_BASE_URL = `${API_BASE}/v1/admin/`;
      const apiUrl = `${API_BASE_URL}private/regenerateQuote/${lead_id}`;
      console.log('📡 Calling regenerateQuote API:', apiUrl);

      const res = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authCtx.token}`
        }
      });

      if (!res.ok) {
        const errorText = await res.text();
        console.error('❌ API Error Response:', errorText);
        throw new Error(`API call failed: ${res.status} - ${errorText}`);
      }

      const result = await res.json();
      console.log('✅ Regenerate Quote API Response:', result);

      if (result.status === 200) {
        // Refresh data
        await fetchQuoteHistory();
        await fetchQuoteFee();

        Swal.fire('Success', 'Original quote restored successfully!', 'success');
      } else {
        Swal.fire('Error', result?.message || 'Failed to regenerate quote', 'error');
      }
    } catch (err) {
      console.error('❌ Error regenerating quote:', err);
      Swal.fire('Error', err?.message || 'Something went wrong', 'error');
    }
  };

  // Fetch email logs/communication history
  const fetchEmailLogs = async () => {
    try {
      setLoadingEmails(true);
      console.log('📧 Fetching email logs for lead:', lead_id);

      const emailLogsResponse = await postRequest('private/getEmailLogs', {
        lead_id: lead_id,
        page: 1,
        limit: 100
      }, authCtx.token);

      console.log('📧 Email logs response:', emailLogsResponse);

      const sentEmails = (emailLogsResponse?.status === 200 || emailLogsResponse?.status === 'success')
        ? (emailLogsResponse.data || [])
        : [];

      setEmailLogs(sentEmails);
      console.log('✅ Email logs loaded:', sentEmails.length);

      setLoadingEmails(false);
    } catch (error) {
      console.error('Error fetching email logs:', error);
      setEmailLogs([]);
      setLoadingEmails(false);
    }
  };

  // Fetch email templates from Automation actions
  const fetchEmailTemplates = async () => {
    try {
      console.log('📧 === FETCHING TRANSFER OF EQUITY AUTOMATION EMAIL TEMPLATES ===');
      console.log('Auth token:', authCtx.token ? 'Present' : 'Missing');
      console.log('Full leadData:', leadData);
      console.log('Lead data product_id:', leadData?.product_id);

      // Get product ID from leadData (handle both object and string)
      let productId = leadData?.product_id?._id || leadData?.product_id;
      console.log('🔍 Product ID from leadData:', productId);

      // If productId is not available, try to fetch it from products API
      if (!productId) {
        console.log('⚠️ Product ID not found in leadData, fetching products...');
        try {
          const productsRes = await postRequest('private/getAllProducts', {}, authCtx.token);
          console.log('📦 Products response:', productsRes);

          if (productsRes?.data) {
            const transferOfEquityProduct = productsRes.data.find(p =>
              p.name?.toLowerCase().includes('transfer') && p.name?.toLowerCase().includes('equity')
            );

            if (transferOfEquityProduct) {
              productId = transferOfEquityProduct._id || transferOfEquityProduct.product_id;
              console.log('✅ Found Transfer of Equity product:', transferOfEquityProduct);
              console.log('✅ Using product ID:', productId);
            } else {
              console.log('⚠️ Transfer of Equity product not found in products list');
            }
          }
        } catch (prodErr) {
          console.log('❌ Error fetching products:', prodErr);
        }
      }

      if (!productId) {
        console.log('❌ No product ID available, cannot fetch email templates');
        setEmailTemplates([]);
        return;
      }

      // Fetch automation actions for this product
      console.log('📤 Fetching automation actions...');

      const automationUrl = `${API_BASE}/v1/admin/automationList`;
      console.log('🌐 Automation API URL:', automationUrl);

      const requestBody = {
        productId: productId,
        page: 1,
        limit: 100
      };
      console.log('📤 Request body:', JSON.stringify(requestBody, null, 2));

      const response = await fetch(automationUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authCtx.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const responseData = await response.json();
      console.log('📡 Automations response:', responseData);
      console.log('📡 Response status:', response.status);
      console.log('📡 Response OK:', response.ok);
      console.log('📡 Response data status:', responseData?.status);

      if (response.ok && (responseData?.status === 200 || responseData?.status === 'success')) {
        const automations = responseData.data?.docs || responseData.data || [];
        console.log('✅ Automations found:', automations.length);
        console.log('✅ Full automations array:', JSON.stringify(automations, null, 2));

        // Extract email templates from automation actions
        const emailTemplates = [];

        automations.forEach((automation, index) => {
          console.log(`🔍 [${index}] Checking automation:`, automation.name);
          console.log(`   Full automation data:`, automation);

          // Check if automation has effect with "Send Email"
          if (automation.effect && automation.effect.type?.toLowerCase().includes('send email')) {
            console.log(`   ✅ Found Send Email effect:`, automation.effect.type);

            const template = {
              _id: automation.effect.emailTemplateId?._id || automation._id,
              name: automation.effect.emailTemplateId?.name || 'Unnamed Template',
              template_id: automation.effect.emailTemplateId?._id,
              automation_id: automation._id,
              effect: automation.effect
            };

            emailTemplates.push(template);
            console.log('   ✅ Added email automation to dropdown:', template.name);
          } else {
            console.log(`   ⏭️  Skipping automation (not Send Email type), effect type:`, automation.effect?.type);
          }
        });

        console.log('📧 Total email templates extracted:', emailTemplates.length);
        console.log('📋 Templates list:', emailTemplates);
        setEmailTemplates(emailTemplates);
      } else {
        console.log('❌ Failed to fetch automations');
        setEmailTemplates([]);
      }
    } catch (error) {
      console.error('❌ Error fetching email templates:', error);
      setEmailTemplates([]);
    }
  };

  // Handle send email
  const handleSendEmail = async (templateId, templateName) => {
    try {
      // First show confirmation
      const confirmResult = await Swal.fire({
        icon: 'question',
        title: 'Send Email?',
        html: `<p>Are you sure you want to send <strong>"${templateName}"</strong> to the customer?</p>`,
        showCancelButton: true,
        confirmButtonText: 'Yes, Send Email',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33'
      });

      // If user cancels, return
      if (!confirmResult.isConfirmed) {
        return;
      }

      setSendingEmail(true);

      console.log('📧 Send Email clicked:', {
        templateId,
        templateName,
        lead_id: lead_id
      });

      // Get customer emails from customersToAdd
      const customerEmails = leadData?.customers_to_add?.map(c => c.email).filter(e => e) ||
                            leadData?.customersToAdd?.map(c => c.email).filter(e => e) || [];

      console.log('📧 Customer emails:', customerEmails);

      if (customerEmails.length === 0) {
        Swal.fire({
          icon: 'error',
          title: 'No Email Address',
          text: 'No customer email address found for this lead.'
        });
        setSendingEmail(false);
        return;
      }

      const response = await postRequest('private/sendEmailFromTemplate', {
        lead_id: lead_id,
        template_id: templateId,
        customer_emails: customerEmails
      }, authCtx.token);

      console.log('Email send response:', response);

      if (response?.status === 200 || response?.status === 'success') {
        Swal.fire({
          icon: 'success',
          title: 'Email Sent',
          text: `Email "${templateName}" has been sent successfully!`
        });
        fetchEmailLogs();
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Failed to Send Email',
          text: response?.message || response?.error || 'Failed to send email. Please try again.'
        });
      }
    } catch (error) {
      console.error('Error sending email:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error?.message || 'Failed to send email. Please try again.'
      });
    } finally {
      setSendingEmail(false);
    }
  };

  // Handle contact attempt update
  const handleUpdateContactAttempt = async (action) => {
    try {
      let newCount = contactAttempts;
      if (action === 'increase') {
        newCount = contactAttempts + 1;
      } else if (action === 'reset') {
        newCount = 0;
      }

      const response = await postRequest('private/updateContactAttempt', {
        lead_id: lead_id,
        contact_attempts: newCount
      }, authCtx.token);

      if (response?.status === 200 || response?.status === 'success') {
        setContactAttempts(newCount);
        Swal.fire({
          icon: 'success',
          title: 'Updated',
          text: `Contact attempts ${action === 'reset' ? 'reset' : 'updated'} successfully!`,
          timer: 1500,
          showConfirmButton: false
        });
      }
    } catch (error) {
      console.error('Error updating contact attempts:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to update contact attempts'
      });
    }
  };

  // Handle view email
  const handleViewEmail = (emailLog) => {
    setSelectedEmail(emailLog);
    setViewEmailModal(true);
  };

  const downloadQuotePDF = () => {
    if (!quoteFeeData) {
      Swal.fire('Error', 'No quote data available to download', 'error');
      return;
    }

    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      const margin = 20;
      const lineHeight = 7;
      let yPos = margin;

      // Generate dynamic quote header
      const now = new Date();
      const formattedDate = now.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
      const formattedTime = now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
      const quoteHeader = `Quote #${lead_id}; Generated ${formattedDate} ${formattedTime}`;

      // Helper to check if new page needed
      const checkNewPage = (requiredSpace = 30) => {
        if (yPos + requiredSpace > pageHeight - margin) {
          doc.addPage();
          yPos = margin;
          return true;
        }
        return false;
      };

      // Header
      doc.setFontSize(9);
      doc.setTextColor(100);
      doc.text(quoteHeader, margin, yPos);
      yPos += lineHeight + 5;

      // Lead ID
      doc.setFontSize(11);
      doc.setTextColor(0);
      doc.setFont('helvetica', 'bold');
      doc.text(`Lead ID: ${lead_id}`, margin, yPos);
      yPos += lineHeight;

      // Title
      doc.setFontSize(16);
      doc.text(`Quote for Transfer of Equity`, margin, yPos);
      yPos += lineHeight + 5;

      // Introduction text
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const introText = 'Thank you for requesting a Transfer of Equity quote from My Law Matters, you will find a complete breakdown of our costs below. We would be delighted to act for you and one of our team will contact you shortly. In the meantime if you have any questions at all then please do get in touch on 0330 124 2171.';
      const introLines = doc.splitTextToSize(introText, pageWidth - 2 * margin);
      introLines.forEach(line => {
        doc.text(line, margin, yPos);
        yPos += lineHeight;
      });
      yPos += 10;

      // Service type heading
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(13, 110, 253);
      doc.text('TRANSFER OF EQUITY', margin, yPos);
      doc.setTextColor(0, 0, 0);
      yPos += lineHeight + 3;

      // Total Cost display
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.text(`Total Cost: £${quoteFeeData.gross.toFixed(2)}`, margin, yPos);
      doc.setFontSize(9);
      doc.text(`(£${quoteFeeData.net.toFixed(2)} + £${quoteFeeData.vat.toFixed(2)} VAT)`, margin + 60, yPos);
      yPos += lineHeight + 5;

      // Table setup
      const colWidths = [90, 30, 30, 30];
      let xPos = margin;

      // Table headers
      doc.setFillColor(240, 240, 240);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);

      const headers = ['Description', 'Net', 'VAT', 'Total'];
      let headerXPos = margin;
      headers.forEach((header, i) => {
        doc.setFillColor(240, 240, 240);
        doc.rect(headerXPos, yPos, colWidths[i], 8, 'F');
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.1);
        doc.rect(headerXPos, yPos, colWidths[i], 8, 'S');
        doc.setTextColor(0, 0, 0);
        doc.text(header, headerXPos + 2, yPos + 5);
        headerXPos += colWidths[i];
      });
      yPos += 10;

      // Get ownership type and conditions
      const ownershipType = quoteFeeData?.ownership_type || leadData?.ownership_type || leadData?.ownershipType || 'Freehold';
      const isMortgaged = leadData?.info?.isMortgaged === true || leadData?.is_mortgaged === true || leadData?.isMortgaged === true;
      const requiresLocalSearchInsurance = leadData?.info?.requiresLocalSearchInsurance === true || leadData?.requires_local_search_insurance === true || leadData?.requiresLocalSearchInsurance === true;

      // Helper function to draw fee row
      const drawFeeRow = (label, amount, vat, gross) => {
        xPos = margin;
        doc.setFont('helvetica', 'normal');

        doc.rect(xPos, yPos, colWidths[0], 8, 'S');
        doc.text(label, xPos + 2, yPos + 5);
        xPos += colWidths[0];

        doc.rect(xPos, yPos, colWidths[1], 8, 'S');
        doc.text(`£${amount.toFixed(2)}`, xPos + 2, yPos + 5);
        xPos += colWidths[1];

        doc.rect(xPos, yPos, colWidths[2], 8, 'S');
        doc.text(`£${vat.toFixed(2)}`, xPos + 2, yPos + 5);
        xPos += colWidths[2];

        doc.rect(xPos, yPos, colWidths[3], 8, 'S');
        doc.text(`£${gross.toFixed(2)}`, xPos + 2, yPos + 5);

        yPos += 8;
      };

      // Legal Fee
      if (quoteFeeData.legalFee) {
        drawFeeRow('Legal Fees', quoteFeeData.legalFee.amount, quoteFeeData.legalFee.vat, quoteFeeData.legalFee.gross);
      }

      // Leasehold Fee (if ownership type is Leasehold)
      if (ownershipType.toLowerCase() === 'leasehold' && quoteFeeData.fixedFees) {
        const leaseholdFee = quoteFeeData.fixedFees.find(fee =>
          fee.label && (fee.label.toLowerCase().includes('leasehold') || fee.label.toLowerCase() === 'leaseholdfee')
        );
        if (leaseholdFee) {
          drawFeeRow('Leasehold Fee', leaseholdFee.amount, leaseholdFee.vat, leaseholdFee.gross);
        }
      }

      // Fixed Fees (excluding Leasehold Fee and conditional Mortgage Admin Fee)
      if (quoteFeeData.fixedFees && quoteFeeData.fixedFees.length > 0) {
        const visibleFixedFees = quoteFeeData.fixedFees.filter(fee => {
          const isLeaseholdFee = fee.label && (fee.label.toLowerCase().includes('leasehold') || fee.label.toLowerCase() === 'leaseholdfee');
          const isMortgageAdminFee = fee.label && (fee.label.toLowerCase().includes('mortgage admin') || fee.label.toLowerCase() === 'mortgageadminfee');

          // Filter out Leasehold Fee (shown separately)
          if (isLeaseholdFee) return false;

          // Filter out Mortgage Admin Fee if not mortgaged
          if (isMortgageAdminFee && !isMortgaged) return false;

          return true;
        });

        visibleFixedFees.forEach(fee => {
          drawFeeRow(formatLabel(fee.label), fee.amount, fee.vat, fee.gross);
        });
      }

      // Disbursements Section
      if (quoteFeeData.disbursements && quoteFeeData.disbursements.length > 0) {
        const visibleDisbursements = quoteFeeData.disbursements.filter(disbursement => {
          const isLocalSearchInsurance = disbursement.label && disbursement.label.toLowerCase().includes('local search insurance');
          const isStampDutyLandTax = disbursement.label && (disbursement.label.toLowerCase().includes('stamp duty land tax') || disbursement.label.toLowerCase().includes('stampdutylandtax'));

          // Filter out Local Search Insurance if not required
          if (isLocalSearchInsurance && !requiresLocalSearchInsurance) return false;

          // Filter out Stamp Duty Land Tax if not mortgaged
          if (isStampDutyLandTax && !isMortgaged) return false;

          return true;
        });

        if (visibleDisbursements.length > 0) {
          // Disbursements section header row
          yPos += 3;
          doc.setFont('helvetica', 'bold');
          xPos = margin;
          doc.setFillColor(240, 240, 240);
          doc.rect(xPos, yPos, colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3], 8, 'F');
          doc.rect(xPos, yPos, colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3], 8, 'S');
          doc.text('Disbursements - Third Party Expenditure', xPos + 2, yPos + 5);
          yPos += 10;

          doc.setFont('helvetica', 'normal');
          visibleDisbursements.forEach(disbursement => {
            drawFeeRow(formatLabel(disbursement.label), disbursement.amount, disbursement.vat, disbursement.gross);
          });
        }
      }

      // Check if we need new page before total row
      checkNewPage(15);

      // TOTAL row with blue background and white text
      yPos += 3;

      // Calculate totals - try different paths in case structure varies
      const totalNet = Number(quoteFeeData?.net || quoteFeeData?.totals?.net || 0);
      const totalVAT = Number(quoteFeeData?.vat || quoteFeeData?.totals?.vat || 0);
      const totalGross = Number(quoteFeeData?.gross || quoteFeeData?.totals?.gross || 0);

      console.log('📊 Transfer_of_Equity PDF Total Values:', {
        net: totalNet,
        vat: totalVAT,
        gross: totalGross,
        quoteFeeDataStructure: quoteFeeData
      });

      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(10);

      xPos = margin;

      // Description cell
      doc.setFillColor(240, 240, 240);
      doc.rect(xPos, yPos, colWidths[0], 8, 'FD');
      doc.setFontSize(8);
      doc.text('TOTAL (Legal Cost + Disbursement Cost)', xPos + 2, yPos + 5);
      xPos += colWidths[0];

      // Net cell
      doc.setFontSize(10);
      doc.setFillColor(240, 240, 240);
      doc.rect(xPos, yPos, colWidths[1], 8, 'FD');
      const netText = `£${totalNet.toFixed(2)}`;
      const netWidth = doc.getTextWidth(netText);
      doc.text(netText, xPos + colWidths[1] - netWidth - 2, yPos + 5);
      xPos += colWidths[1];

      // VAT cell
      doc.setFillColor(240, 240, 240);
      doc.rect(xPos, yPos, colWidths[2], 8, 'FD');
      const vatText = `£${totalVAT.toFixed(2)}`;
      const vatWidth = doc.getTextWidth(vatText);
      doc.text(vatText, xPos + colWidths[2] - vatWidth - 2, yPos + 5);
      xPos += colWidths[2];

      // Total cell
      doc.setFillColor(240, 240, 240);
      doc.rect(xPos, yPos, colWidths[3], 8, 'FD');
      const grossText = `£${totalGross.toFixed(2)}`;
      const grossWidth = doc.getTextWidth(grossText);
      doc.text(grossText, xPos + colWidths[3] - grossWidth - 2, yPos + 5);

      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'normal');
      yPos += 10;

      // Descriptions Section
      yPos += 10;

      // Leasehold Section - Only if ownership type is Leasehold
      if (ownershipType.toLowerCase() === 'leasehold') {
        checkNewPage(40);

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.text('Leasehold', margin, yPos);
        yPos += 8;

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        const leaseholdText = "The purchase of a Leasehold property often involves additional work liaising with the landlord and/or managing agent for which an additional fixed fee is payable. There are also often charges payable to the Landlord on completion for receipting notices of transfer and charge. The fees payable to the landlord and/or managing agent can only be ascertained when enquiries are raised during the transaction and will vary but in most cases will be between £50.00 and £300.00";
        const leaseholdLines = doc.splitTextToSize(leaseholdText, pageWidth - 2 * margin);
        leaseholdLines.forEach(line => {
          doc.text(line, margin, yPos);
          yPos += 5;
        });
        yPos += 10;
      }

      // Mortgaged Section - Only if isMortgaged is true
      if (isMortgaged) {
        checkNewPage(40);

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.text('Mortgaged', margin, yPos);
        yPos += 8;

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        const mortgagedText = "If you are buying a property with the assistance of a mortgage we are often required to act on the mortgage lender's behalf as well as yours and an additional fee for dealing with these formalities is charged.";
        const mortgagedLines = doc.splitTextToSize(mortgagedText, pageWidth - 2 * margin);
        mortgagedLines.forEach(line => {
          doc.text(line, margin, yPos);
          yPos += 5;
        });
        yPos += 10;
      }

      // Identification Section - Always show
      checkNewPage(40);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text('Identification', margin, yPos);
      yPos += 8;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      const identificationText = "In order to comply with Money Laundering regulations we are required to verify the identity of all clients we act for, we therefore carry out an online identification verification service. Money Laundering – in order to comply with Money Laundering legislation, we are required to verify the identity of all clients. You will be asked to provide original copies or certified copies of your identification. These can be brought into the office for copying or posted to us.";
      const identificationLines = doc.splitTextToSize(identificationText, pageWidth - 2 * margin);
      identificationLines.forEach(line => {
        doc.text(line, margin, yPos);
        yPos += 5;
      });

      // Save PDF
      doc.save(`Transfer_of_Equity_Quote_${lead_id}.pdf`);
      Swal.fire('Success', 'Quote PDF downloaded successfully!', 'success');

    } catch (error) {
      console.error('Error generating PDF:', error);
      Swal.fire('Error', 'Failed to generate PDF', 'error');
    }
  };

  if (loading) {
    return (
      <div className="text-center mt-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!leadData) {
    return (
      <div className="text-center mt-5">
        <p>No lead data found</p>
      </div>
    );
  }

  return (
    <>
      <BreadcrumbsWrapper
        items={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Leads', path: '/leads' },
          { label: `View Conveyancing (Transfer of Equity) Lead`, path: '' },
        ]}
      />
      <br />

      <div className='page-header pb-3 mb-3'>
        <div className="d-flex align-items-center justify-content-between">
          <div>
            <h1 className='mt-0 mb-0' style={{ fontSize: '1.5rem', fontWeight: '500' }}>
              Conveyancing (Transfer of Equity)
              <span style={{ color: '#6c757d', marginLeft: '10px' }}>#{lead_id}</span>
            </h1>
          </div>
          <div className="d-flex gap-2">
            {/* Status Badge */}
            {leadData?.status && (
              <span className="badge" style={{
                backgroundColor: leadData.status === 'active' ? '#28a745' : '#6c757d',
                color: 'white',
                padding: '6px 12px',
                fontSize: '0.875rem'
              }}>
                {leadData.status.charAt(0).toUpperCase() + leadData.status.slice(1)}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className='page-top-formview mb-3'>
        <div className='customer-tab-view leads-tab-view'>
          <Tabs defaultActiveKey="overview" id="uncontrolled-tab-example">
            {/* OVERVIEW TAB */}
            <Tab eventKey="overview" title="Overview">
              <div className='mt-3'>

                {/* ------------------------ Quote Box UI ------------------------ */}
                <div className="card shadow-sm mb-4" style={{ borderRadius: '10px', overflow: 'hidden' }}>
                  <div className="card-header" style={{
                    backgroundColor: '#f8f9fa',
                    borderBottom: '2px solid #e9ecef',
                    padding: '1rem 1.25rem'
                  }}>
                    <h5 className="mb-0" style={{ color: '#495057', fontWeight: '600' }}>
                      Quote Summary
                    </h5>
                  </div>
                  <div className="card-body" style={{ padding: '1.5rem' }}>
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-baseline gap-3">
                        <span className="text-muted small">Total Cost</span>
                        <span style={{ color: '#2c3e50', fontWeight: '600', fontSize: '1.2rem' }}>
                          £{(quoteFeeData?.net || leadData?.legal_fee || 0).toFixed(2)} Net + £{(quoteFeeData?.vat || leadData?.vat_amount || 0).toFixed(2)} VAT = £{(quoteFeeData?.gross || leadData?.total_quote || 0).toFixed(2)} Gross
                        </span>
                      </div>
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          console.log('View Quote Detail clicked for Transfer of Equity');
                          setShowQuoteModal(true);
                          fetchQuoteFee();
                        }}
                        style={{
                          color: '#0d6efd',
                          textDecoration: 'none',
                          cursor: 'pointer',
                          fontSize: '14px'
                        }}
                        onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                        onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                      >
                        View Quote Detail
                      </a>
                    </div>


                    <hr className="my-3" />

                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <span className="badge bg-secondary me-2">
                          <i className="fas fa-building me-1"></i>
                          {leadData?.branch_name || leadData?.branch?.name || 'Branch'}
                        </span>
                        <span className="badge bg-info">{leadData?.created_by || localStorage.getItem('username') || 'User'}</span>
                      </div>
                      <div className="text-muted small">
                        {(() => {
                          const date = leadData?.quote_date || leadData?.createdAt || leadData?.created_at;
                          if (!date) return 'Date not available';
                          const dateObj = new Date(date);
                          return `${dateObj.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })} ${dateObj.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
                        })()}
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="card-footer bg-light" style={{ borderTop: '1px solid #e9ecef', padding: '1rem 1.25rem' }}>
                    <div className="d-flex gap-2 flex-wrap align-items-center">
                      <Button
                        variant="contained"
                        className='text-capitalize'
                        onClick={() => {
                          console.log('Revise Quote clicked - Transfer of Equity');
                          setReviseModalShow(true);
                        }}
                        style={{
                          backgroundColor: '#dc3545',
                          borderColor: '#dc3545',
                          fontSize: '0.875rem',
                          padding: '8px 16px'
                        }}
                      >
                        <EditOutlinedIcon fontSize="small" style={{ marginRight: '6px' }} />
                        Revise Quote
                      </Button>
                      <Button
                        variant="outlined"
                        className='text-capitalize'
                        onClick={handleRegenerateQuote}
                        style={{
                          color: '#6c757d',
                          borderColor: '#6c757d',
                          fontSize: '0.875rem',
                          padding: '8px 16px'
                        }}
                      >
                        <i className="fas fa-sync-alt" style={{ marginRight: '6px' }}></i>
                        Regenerate Quote
                      </Button>
                    </div>
                  </div>
                </div>

                <ul className="list-group mb-3">
                  {/* Address */}
                  <li className="list-group-item">
                    <div className="row">
                      <div className="col-4 fw-bold">Address</div>
                      <div className="col-8">
                        {(() => {
                          const address = leadData?.propertyDetails?.address || leadData?.property_address || leadData?.address || leadData?.property;

                          const directAddress = {
                            line1: leadData?.propertyDetails?.address?.line1 || leadData?.address1,
                            line2: leadData?.propertyDetails?.address?.line2 || leadData?.address2,
                            town: leadData?.propertyDetails?.address?.town || leadData?.town,
                            county: leadData?.propertyDetails?.address?.county || leadData?.county || leadData?.country,
                            postcode: leadData?.propertyDetails?.address?.postcode || leadData?.postcode
                          };

                          if (!address && !directAddress.line1) return 'No address specified';

                          if (typeof address === 'string') {
                            return address;
                          } else if (address && typeof address === 'object') {
                            const parts = [];
                            if (address.line1 || address.address1) parts.push(address.line1 || address.address1);
                            if (address.line2 || address.address2) parts.push(address.line2 || address.address2);
                            if (address.town) parts.push(address.town);
                            if (address.county || address.country) parts.push(address.county || address.country);
                            if (address.postcode) parts.push(address.postcode);
                            return parts.length > 0 ? parts.join(', ') : 'No address specified';
                          } else if (directAddress.line1) {
                            const parts = [];
                            if (directAddress.line1) parts.push(directAddress.line1);
                            if (directAddress.line2) parts.push(directAddress.line2);
                            if (directAddress.town) parts.push(directAddress.town);
                            if (directAddress.county) parts.push(directAddress.county);
                            if (directAddress.postcode) parts.push(directAddress.postcode);
                            return parts.join(', ');
                          }

                          return 'No address specified';
                        })()}
                      </div>
                    </div>
                  </li>

                  {/* Customers to Add */}
                  <li className="list-group-item">
                    <div className="row">
                      <div className="col-4 fw-bold">Customers to Add</div>
                      <div className="col-8">
                        {(() => {
                          const customersToAdd = leadData?.customers_to_add || leadData?.customersToAdd || [];
                          if (Array.isArray(customersToAdd) && customersToAdd.length > 0) {
                            return customersToAdd.map((customer, index) => (
                              <div key={customer._id || index}>
                                <a href={`/customers/view/${customer._id}`} className="text-primary text-decoration-none">
                                  {customer.title} {customer.forename} {customer.surname}
                                </a>
                              </div>
                            ));
                          }
                          return <span className="text-muted">No customers to add</span>;
                        })()}
                      </div>
                    </div>
                  </li>

                  {/* Customers to Remove */}
                  <li className="list-group-item">
                    <div className="row">
                      <div className="col-4 fw-bold">Customers to Remove</div>
                      <div className="col-8">
                        {(() => {
                          const customersToRemove = leadData?.customers_to_remove || leadData?.customersToRemove || [];
                          if (Array.isArray(customersToRemove) && customersToRemove.length > 0) {
                            return customersToRemove.map((customer, index) => (
                              <div key={customer._id || index}>
                                <a href={`/customers/view/${customer._id}`} className="text-primary text-decoration-none">
                                  {customer.title} {customer.forename} {customer.surname}
                                </a>
                              </div>
                            ));
                          }
                          return <span className="text-muted">No customers to remove</span>;
                        })()}
                      </div>
                    </div>
                  </li>

                  {/* Property Value */}
                  <li className="list-group-item">
                    <div className="row">
                      <div className="col-4 fw-bold">Property Value</div>
                      <div className="col-8">
                        £{(leadData?.propertyDetails?.propertyValue || leadData?.property_value || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    </div>
                  </li>

                  {/* Type */}
                  <li className="list-group-item">
                    <div className="row">
                      <div className="col-4 fw-bold">Type</div>
                      <div className="col-8">
                        {(() => {
                          const transferType = leadData?.propertyDetails?.transferType || leadData?.transfer_type || leadData?.transferType || 'whole';
                          const displayType = transferType === 'whole' ? 'Whole Transfer' : 'Part Transfer';
                          return displayType;
                        })()}
                      </div>
                    </div>
                  </li>

                  {/* Consideration Value */}
                  <li className="list-group-item">
                    <div className="row">
                      <div className="col-4 fw-bold">Consideration Value</div>
                      <div className="col-8">
                        £{(leadData?.propertyDetails?.considerationValue || leadData?.consideration_value || leadData?.considerationValue || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    </div>
                  </li>

                  {/* Ownership Type */}
                  <li className="list-group-item">
                    <div className="row">
                      <div className="col-4 fw-bold">Ownership Type</div>
                      <div className="col-8">
                        {leadData?.propertyDetails?.ownershipType || leadData?.ownership_type || 'Freehold'}
                      </div>
                    </div>
                  </li>

                  {/* Lender Panel */}
                  {(leadData?.lender_panel_id || leadData?.lender_panel_name) && (
                    <li className="list-group-item">
                      <div className="row">
                        <div className="col-4 fw-bold">Lender Panel</div>
                        <div className="col-8">
                          {(() => {
                            console.log('🔍 Lender Panel check:', {
                              lender_panel_id: leadData?.lender_panel_id,
                              lender_panel_name: leadData?.lender_panel_name,
                              type: typeof leadData?.lender_panel_id
                            });

                            // Check for lender_panel_name field first (from backend aggregation)
                            if (leadData?.lender_panel_name) {
                              return (
                                <div>
                                  <div style={{ color: '#495057' }}>
                                    {leadData.lender_panel_name}
                                  </div>
                                  {leadData?.lender_panel_id?.lender_numbers && leadData.lender_panel_id.lender_numbers.length > 0 && (
                                    <div style={{ color: '#6c757d', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                                      <span style={{ fontWeight: '500' }}>Lender Panel Number: </span>
                                      {leadData.lender_panel_id.lender_numbers.join(', ')}
                                    </div>
                                  )}
                                </div>
                              );
                            }

                            const lenderPanel = leadData?.lender_panel_id;

                            // If lender panel is populated object
                            if (lenderPanel && typeof lenderPanel === 'object' && lenderPanel.name) {
                              return (
                                <div>
                                  <div style={{ color: '#495057' }}>
                                    {lenderPanel.name}
                                  </div>
                                  {lenderPanel?.lender_numbers && lenderPanel.lender_numbers.length > 0 && (
                                    <div style={{ color: '#6c757d', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                                      <span style={{ fontWeight: '500' }}>Lender Panel Number: </span>
                                      {lenderPanel.lender_numbers.join(', ')}
                                    </div>
                                  )}
                                </div>
                              );
                            }

                            // If no lender panel selected
                            return <span className="text-muted">Not specified</span>;
                          })()}
                        </div>
                      </div>
                    </li>
                  )}

                  {/* Partner */}
                  <li className="list-group-item">
                    <div className="row">
                      <div className="col-4 fw-bold">Partner</div>
                      <div className="col-8">
                        {(() => {
                          console.log('🔍 Partner display check:', {
                            partner_id: leadData?.partner_id,
                            partner_name: leadData?.partner_name,
                            type: typeof leadData?.partner_id
                          });

                          if (leadData?.partner_name) {
                            return leadData.partner_name;
                          }

                          if (leadData?.partner_id) {
                            if (typeof leadData.partner_id === 'object' && leadData.partner_id.name) {
                              return leadData.partner_id.name;
                            }
                          }

                          return <span className="text-muted">Not assigned</span>;
                        })()}
                      </div>
                    </div>
                  </li>

                  {/* Info */}
                  <li className="list-group-item">
                    <div className="row">
                      <div className="col-4 fw-bold">Info</div>
                      <div className="col-8">
                        <div className="d-flex flex-wrap gap-1">
                          {(() => {
                            const info = [];
                            if (leadData?.info?.isMortgaged === true || leadData?.is_mortgaged === true || leadData?.isMortgaged === true) {
                              info.push('Is Mortgaged');
                            }
                            if (leadData?.info?.requiresLocalSearchInsurance === true || leadData?.requires_local_search_insurance === true || leadData?.requiresLocalSearchInsurance === true) {
                              info.push('Requires Local Search Insurance');
                            }
                            if (leadData?.info?.
firstTimeBuyer === true || leadData?.
firstTimeBuyer === true || leadData?.
firstTimeBuyer === true) {
                              info.push('Is First Time Buyer');
                            }

                            if (info.length === 0) {
                              return <span className="text-muted">No additional info</span>;
                            }

                            return info.map((item, index) => (
                              <span key={index} className="badge bg-primary me-1">{item}</span>
                            ));
                          })()}
                        </div>
                      </div>
                    </div>
                  </li>

                  {/* Group Code */}
                  <li className="list-group-item">
                    <div className="row">
                      <div className="col-4 fw-bold">Group Code</div>
                      <div className="col-8">
                        {(() => {
                          const groupCode = leadData?.group_code;
                          if (groupCode && typeof groupCode === 'object') {
                            return `${groupCode.name} (${groupCode.code})`;
                          }
                          if (leadData?.group_code_code) {
                            return leadData.group_code_code;
                          }
                          return <span className="text-muted">Not specified</span>;
                        })()}
                      </div>
                    </div>
                  </li>

                  {/* Status */}
                  <li className="list-group-item">
                    <div className="row">
                      <div className="col-4 fw-bold">Status</div>
                      <div className="col-8">
                        {leadData?.status || leadData?.lead_status || 'Pending'}
                      </div>
                    </div>
                  </li>

                  {/* Branch */}
                  <li className="list-group-item">
                    <div className="row">
                      <div className="col-4 fw-bold">Branch</div>
                      <div className="col-8">
                        {quoteFeeData?.branch_name || leadData?.branch_name || 'Not assigned'}
                      </div>
                    </div>
                  </li>
                </ul>

                {/* Edit Button and More Actions */}
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        console.log('Edit button clicked - Transfer of Equity');
                        // Get first customer to add ID if available
                        const firstCustomerId = leadData?.customersToAdd && leadData.customersToAdd.length > 0
                          ? (leadData.customersToAdd[0]._id || leadData.customersToAdd[0].customer_id || leadData.customersToAdd[0])
                          : null;
                        const customerParam = firstCustomerId ? `?customerId=${firstCustomerId}` : '';

                        navigate(`/conveyancing/transfer-of-equity/${leadData?.lead_id || lead_id}${customerParam}`);
                      }}
                      style={{
                        borderColor: '#6c757d',
                        color: '#6c757d',
                        textTransform: 'none',
                        fontSize: '0.875rem'
                      }}
                    >
                      <EditOutlinedIcon fontSize="small" style={{ marginRight: '4px' }} />
                      Edit
                    </Button>
                  </div>

                  <div className="d-flex align-items-center gap-3">
                    <span style={{ fontSize: '0.875rem', color: '#6c757d' }}>
                      Created: {(() => {
                        const date = leadData?.createdAt || leadData?.created_at || leadData?.date_created;
                        if (!date) return 'Date not available';
                        const dateObj = new Date(date);
                        return `${dateObj.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })} ${dateObj.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
                      })()} - {leadData?.created_by || leadData?.createdBy || localStorage.getItem('username') || 'Unknown'}
                    </span>

                    <Dropdown drop="up">
                      <Dropdown.Toggle
                        variant="outline-secondary"
                        size="sm"
                        style={{
                          borderColor: '#6c757d',
                          color: '#6c757d',
                          fontSize: '0.875rem'
                        }}
                      >
                        More Actions
                      </Dropdown.Toggle>

                      <Dropdown.Menu>
                        <Dropdown.Item
                          onClick={() => {
                            navigate('/leads/changestatus', {
                              state: {
                                leadId: lead_id,
                                currentStatus: leadData?.status,
                                returnUrl: window.location.pathname
                              }
                            });
                          }}
                        >
                          <UpdateIcon fontSize="small" style={{ marginRight: '8px' }} />
                          Change Status
                        </Dropdown.Item>

                        <Dropdown.Item
                          onClick={() => {
                            navigate('/leads/changegroupcode', {
                              state: {
                                leadId: lead_id,
                                currentGroupCode: leadData?.group_code,
                                returnUrl: window.location.pathname
                              }
                            });
                          }}
                        >
                          <EditOutlinedIcon fontSize="small" style={{ marginRight: '8px' }} />
                          Change Group Code
                        </Dropdown.Item>

                        <Dropdown.Divider />

                        <Dropdown.Item
                          onClick={() => handleDeleteLead()}
                          style={{ color: '#dc3545' }}
                        >
                          <DeleteOutlineOutlinedIcon fontSize="small" style={{ marginRight: '8px' }} />
                          Delete Lead
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                </div>
              </div>
            </Tab>

            <Tab eventKey="quotes" title="Quotes">
              <div className="mt-3">
                {/* Current Quote Card */}
                {leadData && quoteFeeData && (
                  <div className="card mb-3" style={{ border: '1px solid #dee2e6' }}>
                    <div className="card-body">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                        <h5 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#2c3e50', margin: 0 }}>
                          £{(quoteFeeData?.gross || quoteFeeData?.totals?.gross || 0).toFixed(2)}
                        </h5>
                        <span className="badge bg-primary" style={{ padding: '8px 15px', fontSize: '0.9rem' }}>
                          {quoteFeeData?.branch_name || leadData?.branch_name || 'Branch'}
                        </span>
                      </div>
                      <p style={{ color: '#6c757d', fontSize: '0.95rem', marginBottom: 0 }}>
                        <i className="far fa-calendar-alt me-2"></i>
                        {leadData?.quote_date ? new Date(leadData.quote_date).toLocaleString('en-GB', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : new Date().toLocaleString('en-GB', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                      <Button
                        variant="link"
                        className="text-decoration-none p-0 mt-2"
                        onClick={() => {
                          console.log('View Quote Detail clicked');
                          setShowQuoteModal(true);
                          fetchQuoteFee();
                        }}
                      >
                        View Quote Detail
                      </Button>
                    </div>
                  </div>
                )}

                {/* Quote History Section - Enhanced UI */}
                {quoteHistory && quoteHistory.length > 0 && (
                  <div className="quote-history-section">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <h5 className="mb-0" style={{ fontWeight: 600, color: '#2c3e50' }}>
                        <i className="fas fa-history me-2" style={{ color: '#3498db' }}></i>
                        Quote History
                      </h5>
                      <span className="badge bg-primary" style={{ fontSize: '0.9rem', padding: '6px 12px' }}>
                        {quoteHistory.length} {quoteHistory.length === 1 ? 'Version' : 'Versions'}
                      </span>
                    </div>

                    <div className="timeline" style={{ position: 'relative', paddingLeft: '30px' }}>
                      {/* Timeline line */}
                      <div style={{
                        position: 'absolute',
                        left: '8px',
                        top: '20px',
                        bottom: '20px',
                        width: '2px',
                        backgroundColor: '#e0e0e0'
                      }}></div>

                      {quoteHistory.map((quote, index) => {
                        const totalGross = quote.totals?.total?.gross || 0;
                        const totalNet = quote.totals?.total?.net || 0;
                        const totalVat = quote.totals?.total?.vat || 0;
                        const legalCostGross = quote.totals?.legalCost?.gross || 0;
                        const isActive = quote.is_active || false;
                        const revisionNumber = quote.revision_number || 1;
                        const source = quote.calculation_source || 'api';

                        // Use revision_label directly from API
                        const versionLabel = quote.revision_label || `Revision ${revisionNumber}`;

                        // Format date
                        const displayDate = quote.revised_at || quote.createdAt;

                        // Icon and color based on type
                        let iconClass = 'fa-file-invoice';
                        let iconColor = '#95a5a6';
                        let borderColor = '#e0e0e0';

                        if (isActive) {
                          iconClass = 'fa-check-circle';
                          iconColor = '#27ae60';
                          borderColor = '#27ae60';
                        } else if (versionLabel === 'Original') {
                          iconClass = 'fa-star';
                          iconColor = '#f39c12';
                        } else if (source === 'manual_revision') {
                          iconClass = 'fa-edit';
                          iconColor = '#3498db';
                        }

                        return (
                          <div key={quote._id || index} style={{ position: 'relative', marginBottom: '24px' }}>
                            {/* Timeline dot */}
                            <div style={{
                              position: 'absolute',
                              left: '-26px',
                              top: '16px',
                              width: '16px',
                              height: '16px',
                              borderRadius: '50%',
                              backgroundColor: iconColor,
                              border: '3px solid white',
                              boxShadow: '0 0 0 2px ' + iconColor,
                              zIndex: 1
                            }}></div>

                            {/* Card */}
                            <div className="card" style={{
                              border: `2px solid ${isActive ? borderColor : '#e9ecef'}`,
                              borderRadius: '12px',
                              boxShadow: isActive ? '0 4px 12px rgba(39, 174, 96, 0.15)' : '0 2px 8px rgba(0,0,0,0.08)',
                              transition: 'all 0.3s ease',
                              backgroundColor: isActive ? '#f8fff9' : 'white'
                            }}>
                              <div className="card-body" style={{ padding: '20px' }}>
                                <div className="d-flex justify-content-between align-items-start mb-3">
                                  {/* Left: Version & Amount */}
                                  <div className="flex-grow-1">
                                    <div className="d-flex align-items-center gap-2 mb-2">
                                      <i className={`fas ${iconClass}`} style={{ color: iconColor, fontSize: '18px' }}></i>
                                      <h5 className="mb-0" style={{ fontWeight: 600, color: '#2c3e50' }}>
                                        {versionLabel}
                                      </h5>
                                      {isActive && (
                                        <span className="badge" style={{
                                          backgroundColor: '#27ae60',
                                          color: 'white',
                                          padding: '4px 10px',
                                          borderRadius: '6px',
                                          fontSize: '0.75rem',
                                          fontWeight: 600
                                        }}>
                                          CURRENT
                                        </span>
                                      )}
                                    </div>

                                    {/* Date & Time */}
                                    <p className="text-muted mb-2" style={{ fontSize: '0.85rem' }}>
                                      <i className="far fa-clock me-1"></i>
                                      {new Date(displayDate).toLocaleDateString('en-GB', {
                                        day: '2-digit',
                                        month: 'short',
                                        year: 'numeric'
                                      })} at {new Date(displayDate).toLocaleTimeString('en-GB', {
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })}
                                    </p>

                                    {/* Source Badge */}
                                    <span className="badge" style={{
                                      backgroundColor: source === 'manual_revision' ? '#3498db' : '#95a5a6',
                                      color: 'white',
                                      padding: '4px 8px',
                                      borderRadius: '4px',
                                      fontSize: '0.7rem'
                                    }}>
                                      {source === 'manual_revision' ? 'MANUAL REVISION' :
                                       source === 'api' ? 'AUTO CALCULATED' :
                                       source.toUpperCase()}
                                    </span>
                                  </div>

                                  {/* Right: Branch & Action */}
                                  <div className="d-flex flex-column align-items-end gap-2">
                                    {quote.branch_name && (
                                      <span className="badge" style={{
                                        backgroundColor: '#e8f4f8',
                                        color: '#3498db',
                                        padding: '6px 12px',
                                        borderRadius: '6px',
                                        fontSize: '0.75rem',
                                        fontWeight: 600
                                      }}>
                                        <i className="fas fa-building me-1"></i>
                                        {quote.branch_name}
                                      </span>
                                    )}
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      style={{
                                        borderRadius: '8px',
                                        padding: '6px 16px',
                                        fontSize: '0.8rem',
                                        fontWeight: 500,
                                        borderWidth: '2px'
                                      }}
                                      onClick={() => {
                                        console.log('🔍 View Quote clicked for:', versionLabel);
                                        console.log('📦 Quote data:', quote);

                                        // Transform NESTED format to FLAT format for ViewQuoteDetailModal
                                        const fees = quote.fees || {};
                                        const disbursements = quote.disbursements || {};

                                        // Convert Map to Object if needed
                                        const feesObj = fees instanceof Map ? Object.fromEntries(fees) : fees;
                                        const disbursementsObj = disbursements instanceof Map ? Object.fromEntries(disbursements) : disbursements;

                                        // Calculate totals
                                        const totalVat = quote.totals?.total?.vat || 0;
                                        const totalGross = quote.totals?.total?.gross || 0;

                                        const transformedFeeData = {
                                          branch_name: quote.branch_name,
                                          service_type: quote.service_type,
                                          legal_fee: feesObj.legalFee?.amount || 0,
                                          tt_fee: feesObj.ttFee?.amount || feesObj.telegraphicTransferFee?.amount || 0,
                                          identity_check: feesObj.identityCheck?.amount || 0,
                                          mortgage_admin_fee: feesObj.mortgageAdminFee?.amount || 0,
                                          leasehold_fee: feesObj.leaseholdFee?.amount || 0,
                                          hm_land_registry_fee: disbursementsObj.hmLandRegistryFee?.amount || 0,
                                          land_registry_search: disbursementsObj.landRegistrySearch?.amount || 0,
                                          land_charges_search: disbursementsObj.landChargesSearch?.amount || 0,
                                          official_copies: disbursementsObj.officialCopies?.amount || 0,
                                          vat_amount: totalVat,
                                          total_quote: totalGross,
                                          all_fees: feesObj,
                                          all_disbursements: disbursementsObj,
                                          totals: quote.totals
                                        };

                                        console.log('✅ Transformed fee data:', transformedFeeData);

                                        setSelectedQuote({
                                          legalFeeData: transformedFeeData,
                                          leadData: leadData,
                                          revision_label: versionLabel,
                                          is_history: !isActive
                                        });
                                        setShowQuoteDetailModal(true);
                                      }}
                                    >
                                      <VisibilityIcon fontSize="small" style={{ fontSize: '14px', marginRight: '4px' }} />
                                      View Details
                                    </Button>
                                  </div>
                                </div>

                                {/* Pricing Breakdown */}
                                <div className="mt-3 pt-3" style={{ borderTop: '1px solid #e9ecef' }}>
                                  <div className="row g-3">
                                    {/* Total Amount - Highlighted */}
                                    <div className="col-md-4">
                                      <div style={{
                                        backgroundColor: isActive ? '#e8f5e9' : '#f8f9fa',
                                        padding: '12px',
                                        borderRadius: '8px',
                                        textAlign: 'center'
                                      }}>
                                        <p className="text-muted mb-1" style={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase' }}>
                                          Total Amount
                                        </p>
                                        <h4 className="mb-0" style={{ color: isActive ? '#27ae60' : '#2c3e50', fontWeight: 700 }}>
                                          £{Number(totalGross).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </h4>
                                      </div>
                                    </div>

                                    {/* Legal Costs */}
                                    <div className="col-md-4">
                                      <div style={{ padding: '12px' }}>
                                        <p className="text-muted mb-1" style={{ fontSize: '0.75rem' }}>
                                          <i className="fas fa-balance-scale me-1"></i>
                                          Legal Costs
                                        </p>
                                        <h6 className="mb-0" style={{ color: '#2c3e50', fontWeight: 600 }}>
                                          £{Number(legalCostGross).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </h6>
                                      </div>
                                    </div>

                                    {/* VAT */}
                                    <div className="col-md-4">
                                      <div style={{ padding: '12px' }}>
                                        <p className="text-muted mb-1" style={{ fontSize: '0.75rem' }}>
                                          <i className="fas fa-receipt me-1"></i>
                                          VAT Included
                                        </p>
                                        <h6 className="mb-0" style={{ color: '#2c3e50', fontWeight: 600 }}>
                                          £{Number(totalVat).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </h6>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* No quotes message */}
                {(!leadData || !quoteFeeData) && (!quoteHistory || quoteHistory.length === 0) && (
                  <div className="text-center py-5">
                    <i className="fas fa-file-invoice fa-3x text-muted mb-3"></i>
                    <h5 className="text-muted">No Quotes Available</h5>
                    <p className="text-muted">No quotes have been generated yet.</p>
                  </div>
                )}
              </div>
            </Tab>

            <Tab eventKey="notes" title="Notes">
              <Notes lead_id={lead_id} product_id={leadData?.product_id} />
            </Tab>

            <Tab eventKey="communication" title="Communication">
              <div className='communication-table'>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <Dropdown>
                    <Dropdown.Toggle variant="secondary" id="dropdown-basic">
                      Contact Attempt ({contactAttempts})
                    </Dropdown.Toggle>

                    <Dropdown.Menu>
                      <Dropdown.Item onClick={() => handleUpdateContactAttempt('increase')}>
                        <MailOutlineIcon fontSize="small" /> &nbsp; Increase Contact Attempt Count
                      </Dropdown.Item>
                      <Dropdown.Item onClick={() => handleUpdateContactAttempt('reset')}>
                        <CallOutlinedIcon fontSize="small" /> &nbsp; Reset
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>

                  {/* Send Email Dropdown - Right Aligned */}
                  <div className="d-flex align-items-center gap-2">
                    <Dropdown>
                      <Dropdown.Toggle variant="primary" id="send-email-dropdown" disabled={sendingEmail}>
                        {sendingEmail ? 'Sending...' : 'Send Email'}
                      </Dropdown.Toggle>

                      <Dropdown.Menu>
                        {emailTemplates.length === 0 ? (
                          <Dropdown.Item disabled>No templates available</Dropdown.Item>
                        ) : (
                          emailTemplates.map((template) => (
                            <Dropdown.Item
                              key={template._id}
                              onClick={() => handleSendEmail(template._id, template.name)}
                            >
                              {template.name}
                            </Dropdown.Item>
                          ))
                        )}
                      </Dropdown.Menu>
                    </Dropdown>

                    {/* Refresh Button */}
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => {
                        console.log('🔄 Manual refresh triggered');
                        fetchEmailTemplates();
                        fetchEmailLogs();
                      }}
                      title="Refresh templates and logs"
                    >
                      <RefreshIcon fontSize="small" />
                    </Button>
                  </div>
                </div>

                {/* Email History Table */}
                <div className="mt-4">
                  <Table striped responsive bordered>
                    <thead>
                      <tr>
                        <th>Sent</th>
                        <th>Customer</th>
                        <th>Content</th>
                        <th style={{ width: '80px', textAlign: 'center' }}>View</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loadingEmails ? (
                        <tr>
                          <td colSpan="4" className="text-center">Loading...</td>
                        </tr>
                      ) : emailLogs.length === 0 ? (
                        <tr>
                          <td colSpan="4" className="text-center">No communication logs found</td>
                        </tr>
                      ) : (
                        emailLogs.map((log, index) => (
                          <tr key={log._id || index}>
                            <td>
                              {log.sent_at ? new Date(log.sent_at).toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              }) : (log.isAutomation ? <span className="text-muted">Not triggered</span> : 'N/A')}
                            </td>
                            <td>
                              <div>
                                {log.recipient_name || log.customer_id?.forename || 'Unknown'}
                                <div className="text-muted small">{log.recipient_email}</div>
                              </div>
                            </td>
                            <td>
                              <div>
                                <strong>{log.template_name || log.subject || 'No title'}</strong>
                                <div className="text-muted small">
                                  {log.subject && log.subject !== log.template_name && (
                                    <span>Subject: {log.subject}</span>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleViewEmail(log)}
                                title="View email content"
                                sx={{ minWidth: '40px', padding: '4px 8px' }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              </div>
            </Tab>

            <Tab eventKey="tasks" title="Tasks">
              <div className="mt-3">
                <p>Tasks will appear here.</p>
              </div>
            </Tab>
          </Tabs>
        </div>
      </div>

      {/* View Quote Modal */}
      <Modal show={showQuoteModal} onHide={() => setShowQuoteModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>View Quote</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingFee ? (
            <div className="text-center py-3">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading quote details...</p>
            </div>
          ) : quoteFeeData ? (
            <>
              <Table bordered>
                <thead>
                  <tr>
                    <th style={{ width: '50%' }}>Description</th>
                    <th className="text-end" style={{ width: '16.66%' }}>NET</th>
                    <th className="text-end" style={{ width: '16.66%' }}>VAT</th>
                    <th className="text-end" style={{ width: '16.66%' }}>GROSS</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Legal Cost Section */}
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <td colSpan="4"><strong>Legal Cost</strong></td>
                  </tr>

                  {/* Legal Fee */}
                  {quoteFeeData.legalFee && (
                    <tr>
                      <td>Legal Fees</td>
                      <td className="text-end">£{quoteFeeData.legalFee.amount.toFixed(2)}</td>
                      <td className="text-end">£{quoteFeeData.legalFee.vat.toFixed(2)}</td>
                      <td className="text-end">£{quoteFeeData.legalFee.gross.toFixed(2)}</td>
                    </tr>
                  )}

                  {/* HM Land Registry Fee (from wholeTitleFee) */}
                  {quoteFeeData.hmLandRegistryFee && (
                    <tr>
                      <td>HM Land Registry Fee</td>
                      <td className="text-end">£{quoteFeeData.hmLandRegistryFee.amount.toFixed(2)}</td>
                      <td className="text-end">£{quoteFeeData.hmLandRegistryFee.vat.toFixed(2)}</td>
                      <td className="text-end">£{quoteFeeData.hmLandRegistryFee.gross.toFixed(2)}</td>
                    </tr>
                  )}

                  {/* All Fixed Fees (Leasehold, Mortgage Admin, TT, Identity Check, etc.) */}
                  {quoteFeeData.fixedFees && quoteFeeData.fixedFees.length > 0 && (() => {
                    const isMortgaged = leadData?.info?.isMortgaged === true || leadData?.is_mortgaged === true || leadData?.isMortgaged === true;
                    const ownershipType = quoteFeeData?.ownership_type || leadData?.ownership_type || leadData?.ownershipType || leadData?.propertyDetails?.ownershipType || 'Freehold';

                    // Filter visible fees based on conditions
                    const visibleFees = quoteFeeData.fixedFees.filter(fee => {
                      const isMortgageAdminFee = fee.label && (
                        fee.label.toLowerCase().includes('mortgage admin') ||
                        fee.label.toLowerCase() === 'mortgageadminfee'
                      );

                      const isLeaseholdFee = fee.label && (
                        fee.label.toLowerCase().includes('leasehold') ||
                        fee.label.toLowerCase() === 'leaseholdfee'
                      );

                      // Skip Mortgage Admin Fee if not mortgaged
                      if (isMortgageAdminFee && !isMortgaged) {
                        return false;
                      }

                      // Skip Leasehold Fee if not leasehold
                      if (isLeaseholdFee && ownershipType.toLowerCase() !== 'leasehold') {
                        return false;
                      }

                      return true;
                    });

                    return visibleFees.map((fee, index) => (
                      <tr key={`fixed-${index}`}>
                        <td>{formatLabel(fee.label)}</td>
                        <td className="text-end">£{fee.amount.toFixed(2)}</td>
                        <td className="text-end">£{fee.vat.toFixed(2)}</td>
                        <td className="text-end">£{fee.gross.toFixed(2)}</td>
                      </tr>
                    ));
                  })()}

                  {/* Total Legal Cost */}
                  {(() => {
                    let legalCostNet = quoteFeeData.legalFee?.amount || 0;
                    let legalCostVat = quoteFeeData.legalFee?.vat || 0;
                    let legalCostGross = quoteFeeData.legalFee?.gross || 0;

                    // Add HM Land Registry Fee to Legal Cost
                    if (quoteFeeData.hmLandRegistryFee) {
                      legalCostNet += quoteFeeData.hmLandRegistryFee.amount || 0;
                      legalCostVat += quoteFeeData.hmLandRegistryFee.vat || 0;
                      legalCostGross += quoteFeeData.hmLandRegistryFee.gross || 0;
                    }

                    const isMortgaged = leadData?.info?.isMortgaged === true || leadData?.is_mortgaged === true || leadData?.isMortgaged === true;
                    const ownershipType = quoteFeeData?.ownership_type || leadData?.ownership_type || leadData?.ownershipType || leadData?.propertyDetails?.ownershipType || 'Freehold';

                    if (quoteFeeData.fixedFees) {
                      quoteFeeData.fixedFees.forEach(fee => {
                        const isMortgageAdminFee = fee.label && fee.label.toLowerCase().includes('mortgage admin');
                        const isLeaseholdFee = fee.label && fee.label.toLowerCase().includes('leasehold');

                        // Include if conditions are met
                        if (!(isMortgageAdminFee && !isMortgaged) && !(isLeaseholdFee && ownershipType.toLowerCase() !== 'leasehold')) {
                          legalCostNet += fee.amount || 0;
                          legalCostVat += fee.vat || 0;
                          legalCostGross += fee.gross || 0;
                        }
                      });
                    }

                    return (
                      <tr style={{ backgroundColor: '#e3f2fd', fontWeight: '600' }}>
                        <td>TOTAL LEGAL COST</td>
                        <td className="text-end">£{legalCostNet.toFixed(2)}</td>
                        <td className="text-end">£{legalCostVat.toFixed(2)}</td>
                        <td className="text-end">£{legalCostGross.toFixed(2)}</td>
                      </tr>
                    );
                  })()}

                  {/* Disbursement - Third Party Expenditure Section */}
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <td colSpan="4"><strong>Disbursement - Third Party Expenditure</strong></td>
                  </tr>

                  {/* All Disbursements (HM Land Registry, Land Registry Search, Land Charges Search, Official Copies) */}
                  {quoteFeeData.disbursements && quoteFeeData.disbursements.length > 0 && (() => {
                    const isMortgaged = leadData?.info?.isMortgaged === true || leadData?.is_mortgaged === true || leadData?.isMortgaged === true;
                    const requiresLocalSearchInsurance = leadData?.info?.requiresLocalSearchInsurance === true || leadData?.requires_local_search_insurance === true || leadData?.requiresLocalSearchInsurance === true;

                    // Filter visible disbursements
                    const visibleDisbursements = quoteFeeData.disbursements.filter(disbursement => {
                      const isLocalSearchInsurance = disbursement.label.toLowerCase().includes('local search insurance');
                      const isStampDutyLandTax = disbursement.label.toLowerCase().includes('stamp duty land tax') || disbursement.label.toLowerCase().includes('stampdutylandtax');

                      // Skip Local Search Insurance if not required
                      if (isLocalSearchInsurance && !requiresLocalSearchInsurance) {
                        return false;
                      }

                      // Skip Stamp Duty Land Tax if not mortgaged
                      if (isStampDutyLandTax && !isMortgaged) {
                        return false;
                      }

                      return true;
                    });

                    return visibleDisbursements.map((disbursement, index) => (
                      <tr key={`disbursement-${index}`}>
                        <td>{formatLabel(disbursement.label)}</td>
                        <td className="text-end">£{disbursement.amount.toFixed(2)}</td>
                        <td className="text-end">{disbursement.vat > 0 ? `£${disbursement.vat.toFixed(2)}` : '-'}</td>
                        <td className="text-end">£{disbursement.gross.toFixed(2)}</td>
                      </tr>
                    ));
                  })()}

                  {/* Total Disbursement Cost */}
                  {(() => {
                    let disbursementNet = 0;
                    let disbursementVat = 0;
                    let disbursementGross = 0;

                    const isMortgaged = leadData?.info?.isMortgaged === true || leadData?.is_mortgaged === true || leadData?.isMortgaged === true;
                    const requiresLocalSearchInsurance = leadData?.info?.requiresLocalSearchInsurance === true || leadData?.requires_local_search_insurance === true || leadData?.requiresLocalSearchInsurance === true;

                    if (quoteFeeData.disbursements) {
                      quoteFeeData.disbursements.forEach(disbursement => {
                        const isLocalSearchInsurance = disbursement.label.toLowerCase().includes('local search insurance');
                        const isStampDutyLandTax = disbursement.label.toLowerCase().includes('stamp duty land tax');

                        // Include if conditions are met
                        if (!(isLocalSearchInsurance && !requiresLocalSearchInsurance) && !(isStampDutyLandTax && !isMortgaged)) {
                          disbursementNet += disbursement.amount || 0;
                          disbursementVat += disbursement.vat || 0;
                          disbursementGross += disbursement.gross || 0;
                        }
                      });
                    }

                    return (
                      <tr style={{ backgroundColor: '#e3f2fd', fontWeight: '600' }}>
                        <td>TOTAL DISBURSEMENT COST</td>
                        <td className="text-end">£{disbursementNet.toFixed(2)}</td>
                        <td className="text-end">-</td>
                        <td className="text-end">£{disbursementGross.toFixed(2)}</td>
                      </tr>
                    );
                  })()}

                  {/* Grand Total */}
                  <tr style={{ backgroundColor: '#172554', fontWeight: '700', color: 'white' }}>
                    <td style={{ color: 'white', fontWeight: '700' }}>TOTAL (Legal Cost + Disbursement Cost)</td>
                    <td className="text-end" style={{ color: 'white' }}>£{quoteFeeData.net.toFixed(2)}</td>
                    <td className="text-end" style={{ color: 'white' }}>£{quoteFeeData.vat.toFixed(2)}</td>
                    <td className="text-end" style={{ color: 'white' }}>£{quoteFeeData.gross.toFixed(2)}</td>
                  </tr>
                </tbody>
              </Table>

              {/* Leasehold Section - Only show if ownership type is Leasehold */}
              {(() => {
                const ownershipType = quoteFeeData?.ownership_type || leadData?.ownership_type || leadData?.ownershipType || leadData?.propertyDetails?.ownershipType || 'Freehold';

                if (ownershipType.toLowerCase() === 'leasehold') {
                  return (
                    <div className="mt-4">
                      <h6 style={{ fontWeight: 'bold' }}>Leasehold</h6>
                      <p style={{ fontSize: '0.875rem', color: '#6c757d', marginBottom: '1rem' }}>
                        The purchase of a Leasehold property often involves additional work liaising with the landlord and/or managing agent for which an additional fixed fee is payable. There are also often charges payable to the Landlord on completion for receipting notices of transfer and charge. The fees payable to the landlord and/or managing agent can only be ascertained when enquiries are raised during the transaction and will vary but in most cases will be between £50.00 and £300.00
                      </p>
                    </div>
                  );
                }
                return null;
              })()}

              {/* Mortgaged Section - Only show if isMortgaged is true */}
              {(() => {
                const isMortgaged = leadData?.info?.isMortgaged === true || leadData?.is_mortgaged === true || leadData?.isMortgaged === true;

                if (isMortgaged) {
                  return (
                    <div className={quoteFeeData?.ownership_type?.toLowerCase() === 'leasehold' || leadData?.ownership_type?.toLowerCase() === 'leasehold' || leadData?.ownershipType?.toLowerCase() === 'leasehold' ? "mt-3" : "mt-4"}>
                      <h6 style={{ fontWeight: 'bold' }}>Mortgaged</h6>
                      <p style={{ fontSize: '0.875rem', color: '#6c757d', marginBottom: '1rem' }}>
                        If you are buying a property with the assistance of a mortgage we are often required to act on the mortgage lender's behalf as well as yours and an additional fee for dealing with these formalities is charged.
                      </p>
                    </div>
                  );
                }
                return null;
              })()}

              {/* Identification Section - Always show */}
              <div className={(() => {
                const ownershipType = quoteFeeData?.ownership_type || leadData?.ownership_type || leadData?.ownershipType || leadData?.propertyDetails?.ownershipType || 'Freehold';
                const isMortgaged = leadData?.info?.isMortgaged === true || leadData?.is_mortgaged === true || leadData?.isMortgaged === true;

                // If either Leasehold or Mortgaged sections are shown, use mt-3, otherwise mt-4
                if (ownershipType.toLowerCase() === 'leasehold' || isMortgaged) {
                  return "mt-3";
                }
                return "mt-4";
              })()}>
                <h6 style={{ fontWeight: 'bold' }}>Identification</h6>
                <p style={{ fontSize: '0.875rem', color: '#6c757d', marginBottom: '0' }}>
                  In order to comply with Money Laundering regulations we are required to verify the identity of all clients we act for, we therefore carry out an online identification verification service. Money Laundering – in order to comply with Money Laundering legislation, we are required to verify the identity of all clients. You will be asked to provide original copies or certified copies of your identification. These can be brought into the office for copying or posted to us.
                </p>
              </div>
            </>
          ) : (
            <div className="text-center py-3">
              <p className="text-muted">No quote data available</p>
            </div>
          )}

          <div className="d-flex justify-content-end mt-3">
            <Button
              variant="outlined"
              size="small"
              onClick={downloadQuotePDF}
              disabled={!quoteFeeData}
              style={{
                borderColor: '#6c757d',
                color: '#6c757d',
                textTransform: 'none'
              }}
            >
              <i className="fas fa-download me-2"></i>
              Download
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* View Email Modal */}
      <Modal show={viewEmailModal} onHide={() => setViewEmailModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>View Email</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedEmail ? (
            <div>
              <div
                dangerouslySetInnerHTML={{ __html: selectedEmail.content || 'No content available' }}
                style={{
                  maxHeight: '500px',
                  overflowY: 'auto',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              />
            </div>
          ) : (
            <div className="text-center py-3">
              <p className="text-muted">No email selected</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outlined" onClick={() => setViewEmailModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Revise Quote Modal */}
      <ReviseQuoteModal
        show={reviseModalShow}
        onHide={() => setReviseModalShow(false)}
        leadData={leadData}
        authToken={authCtx.token}
        onReviseQuote={handleReviseQuote}
        quoteFeeData={quoteFeeData}
      />

      {/* View Quote Detail Modal */}
      <ViewQuoteDetailModal
        show={showQuoteDetailModal}
        onHide={() => {
          console.log('Closing ViewQuoteDetailModal');
          setShowQuoteDetailModal(false);
          setSelectedQuote(null);
        }}
        leadId={leadData?._id || lead_id}
        authToken={authCtx.token}
        legalFeeData={selectedQuote?.legalFeeData || quoteFeeData}
        leadData={selectedQuote?.leadData || leadData}
        calculationTotals={selectedQuote?.legalFeeData?.totals || null}
      />
    </>
  );
};

export default ViewLeadTransferOfEquity;
